import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { authAPI, blogsAPI, generationAPI, templatesAPI } from '../services/api';
import ArticleStructureEditor from '../components/ArticleStructureEditor';
import TopicAndDateRow from '../components/TopicAndDateRow';
import TetrisGame from '../components/TetrisGame';
import { ASPECT_RATIOS, DEFAULT_ASPECT_RATIO, ratioToCss } from '../constants/aspectRatios';
import './BlogWriter.css';

const DEFAULT_AI_PROVIDERS = [
    { id: 'gemini', name: 'Gemini', available: true, icon: '🔮' },
    { id: 'openai', name: 'OpenAI', available: false, icon: '🤖' },
    { id: 'claude', name: 'Claude', available: false, icon: '🧠' },
    { id: 'xai', name: 'xAI (Grok)', available: false, icon: '⚡' }
];

// Default styles fallback
const DEFAULT_IMAGE_STYLES = [
    { id: 'photorealistic', short_name: 'photorealistic', name: 'Photorealistic', icon: '📷', description: 'High-quality realistic photography', is_enabled: true },
    { id: 'cinematic', short_name: 'cinematic', name: 'Cinematic', icon: '🎬', description: 'Movie-like dramatic visuals', is_enabled: true },
    { id: 'animated', short_name: 'animated', name: 'Animated', icon: '🎨', description: 'Stylized cartoon style', is_enabled: true },
    { id: 'infographic', short_name: 'infographic', name: 'Infographic', icon: '📊', description: 'Clean data visualization', is_enabled: true },
    { id: 'editorial', short_name: 'editorial', name: 'Editorial', icon: '📰', description: 'Magazine-style imagery', is_enabled: true }
];

// Style icons mapping
const STYLE_ICONS = {
    'cinematic_editorial': '🎬',
    'corporate_infographic': '📊',
    'direct_response_promotio': '📢',
    'authority_documentary': '📸',
    'photorealistic': '📷',
    'cinematic': '🎬',
    'animated': '🎨',
    'infographic': '📊',
    'editorial': '📰'
};

// Backend ResearchSettings.perplexity_mode accepts only strict|mixed|default
function _normalizePerplexityModeForState(v) {
    const m = (v == null ? '' : String(v)).trim().toLowerCase();
    if (m === 'mix') return 'mixed';
    if (m === 'unrestricted' || m === '') return 'default';
    return ['strict', 'mixed', 'default'].includes(m) ? m : 'default';
}

const BLOCK_WORD_COUNT_OPTIONS = [100, 150, 200, 250, 300, 350, 400, 500, 700, 1000, 1250, 1500, 2000];

const SECTION_LABELS = {
    tone_of_voice: 'Tone of Voice',
    writing_style: 'Writing Style',
    emphasis_formatting: 'Emphasis & Formatting',
    language_patterns: 'Language Patterns',
    argumentation_style: 'Argumentation Style',
    full: 'All Sections'
};

const PERSONA_TEMPLATE = {
    author_name: '',
    persona_title: '',
    voice_description: '',
    reader_profile: '',
    coverage_focus: '',
    signature_moves: ''
};

const PERSONA_FIELDS = [
    { key: 'author_name', label: 'Persona Name', hint: 'The public pen name/byline the article should use', type: 'input' },
    { key: 'persona_title', label: 'Persona Role', hint: 'Short descriptor (e.g., Cinematic Financial Essayist)', type: 'input' },
    { key: 'voice_description', label: 'Voice & POV', hint: 'How this persona sounds, pacing, emotional register' },
    { key: 'reader_profile', label: 'Audience Blueprint', hint: 'Who this persona writes for and what they expect' },
    { key: 'coverage_focus', label: 'Beat Coverage', hint: 'Topics, industries, and macro focus areas' },
    { key: 'signature_moves', label: 'Signature Moves', hint: 'Repeatable devices, transitions, or rhetorical patterns' }
];

const GENERATION_STEPS = [
    { key: 'queued', label: 'Queued' },
    { key: 'searching_context', label: 'Searching news (Perplexity)' },
    { key: 'awaiting_news_selection', label: 'News Review' },
    { key: 'generating_text', label: 'Drafting article' },
    { key: 'readability_check', label: 'Readability check' },
    { key: 'rewriting', label: 'Auto-rewrite' },
    { key: 'processing', label: 'Structuring output' },
    { key: 'images', label: 'Rendering images' },
    { key: 'completed', label: 'Complete' },
    { key: 'failed', label: 'Failed' }
];

const READABILITY_DISPLAY_METRICS = [
    { key: 'flesch_reading_ease', label: 'Flesch Reading Ease', decimals: 1 },
    { key: 'fk_grade', label: 'FK Grade Level', decimals: 1 },
    { key: 'words_per_sentence', label: 'Words per Sentence', decimals: 1 },
    { key: 'complex_words_pct', label: 'Complex Words %', decimals: 1, suffix: '%' },
    { key: 'passive_voice_pct', label: 'Passive Voice %', decimals: 1, suffix: '%' },
];

const GENERATION_FAILSAFE_MS = 10 * 60 * 1000;

const sanitizeShortName = (value = '') =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 30);

const decorateStyle = (style) => ({
    ...style,
    icon: STYLE_ICONS[style.short_name] || style.icon || '🖼️'
});

const formatMetricValue = (value, decimals = 1, suffix = '') => {
    const num = Number(value);
    if (Number.isFinite(num)) {
        const formatted = parseFloat(num.toFixed(decimals));
        return `${formatted}${suffix}`;
    }
    if (value === 0) return `0${suffix}`;
    return value != null ? `${value}${suffix}` : '—';
};

/** Parse article content for block markers and return ordered list of { key, text } */
function parseOrderedBlocks(content, blocks) {
    if (!content || typeof content !== 'string') return [];
    const keys = [];
    content.split('\n').forEach((line) => {
        const m = line.match(/^===([^=]+)===$/);
        if (m) keys.push(m[1].trim());
    });
    const blockMap = blocks && typeof blocks === 'object' ? blocks : {};
    return keys.map((key) => ({ key, text: blockMap[key] || '' }));
}

/** Build email-preview items: hero slot, then for each block text + ALL images (generated + real) for that block */
function buildEmailPreviewItems(orderedBlocks, images, imageUrls, realImages = [], realImageUrls = {}) {
    const items = [];
    const sorted = [...(images || [])].sort((a, b) => (a.image_number || 0) - (b.image_number || 0));
    const heroImages = sorted.filter((img) => !img.block_key && (img.image_number || 0) <= 2);
    const blockImages = sorted.filter((img) => (img.image_number || 0) > 2 || (img.block_key && (img.image_number || 0) > 0));
    const blockKeysInContent = new Set((orderedBlocks || []).filter((b) => (b.key || '').startsWith('BLOCK_')).map((b) => b.key));
    const byBlockKey = {};
    (blockImages || []).forEach((img) => {
        const key = img.block_key || `BLOCK_${(img.image_number || 0) - 2}`;
        if (!byBlockKey[key]) byBlockKey[key] = [];
        byBlockKey[key].push(img);
    });
    Object.keys(byBlockKey).forEach((k) => byBlockKey[k].sort((a, b) => (a.image_number || 0) - (b.image_number || 0)));

    const realHero = (realImages || []).filter((img) => !img.block_key);
    const realByBlock = {};
    (realImages || []).forEach((img) => {
        if (img.block_key) {
            if (!realByBlock[img.block_key]) realByBlock[img.block_key] = [];
            realByBlock[img.block_key].push(img);
        }
    });

    if (heroImages.length) {
        items.push({
            type: 'hero',
            images: heroImages.map((img, i) => ({ ...img, _placementLabel: `Hero ${i + 1} (generated)` }))
        });
    }
    realHero.forEach((img, i) => {
            items.push({
                type: 'image_real',
                image: img,
                filename: img.file_path ? img.file_path.split(/[/\\]/).pop() : '',
                placementLabel: `Hero / real ${i + 1}`
            });
        });
    (orderedBlocks || []).forEach(({ key, text }) => {
        items.push({ type: 'text', key, text });
        if (!key.startsWith('BLOCK_')) return;
        const blockImgs = byBlockKey[key] || [];
        blockImgs.forEach((img, i) => {
            items.push({
                type: 'image',
                image: img,
                filename: img.file_path ? img.file_path.split(/[/\\]/).pop() : '',
                placementLabel: blockImgs.length > 1 ? `${key} (${i + 1}/${blockImgs.length})` : `After ${key}`
            });
        });
        (realByBlock[key] || []).forEach((img, i) => {
            items.push({
                type: 'image_real',
                image: img,
                filename: img.file_path ? img.file_path.split(/[/\\]/).pop() : '',
                placementLabel: `After ${key} (real)`
            });
        });
    });
    const unplaced = blockImages.filter((img) => {
        const key = img.block_key || `BLOCK_${(img.image_number || 0) - 2}`;
        return !blockKeysInContent.has(key);
    });
    unplaced.forEach((img, i) => {
        items.push({
            type: 'image',
            image: img,
            filename: img.file_path ? img.file_path.split(/[/\\]/).pop() : '',
            placementLabel: `After article (${i + 1}/${unplaced.length})`
        });
    });
    const realUnplaced = (realImages || []).filter((img) => img.block_key && !blockKeysInContent.has(img.block_key));
    realUnplaced.forEach((img, i) => {
        items.push({
            type: 'image_real',
            image: img,
            filename: img.file_path ? img.file_path.split(/[/\\]/).pop() : '',
            placementLabel: `Real photo (${i + 1})`
        });
    });
    return items;
}

const formatStyleMessage = (text, role) => {
    if (!text) return '';
    const normalized = text.replace(/\r\n/g, '\n').trim();
    if (role !== 'assistant') {
        return normalized;
    }
    const withBreaks = normalized.replace(/(\*\*[^*]+:\*\*)/g, '\n$1');
    return withBreaks.replace(/\n{3,}/g, '\n\n').trim();
};

const stripUserEcho = (text, userMessage) => {
    if (!text || !userMessage) return text || '';
    const trimmedText = text.trim();
    const trimmedUser = userMessage.trim();
    if (!trimmedUser) return trimmedText;
    if (trimmedText.toLowerCase().startsWith(trimmedUser.toLowerCase())) {
        return trimmedText.slice(trimmedUser.length).trim();
    }
    return trimmedText;
};

const computeContentStats = (content = '') => {
    if (!content) return null;
    const original = content.trim();
    if (!original) return null;

    // Strip generation markers
    let cleaned = content
        .replace(/===HEADER_\d+===\s*/g, '')
        .replace(/===HEADER===\s*/g, '')
        .replace(/===BLOCK_\d+===\s*/g, '')
        .replace(/===BLOCK===\s*/g, '')
        .replace(/===PROMO_\d+===\s*/g, '')
        .replace(/===PROMO===\s*/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    if (!cleaned) cleaned = original;

    const cleanWordCount = cleaned ? cleaned.split(/\s+/).length : 0;

    // Promo word counting by section
    let promoWordCount = 0;
    let inPromo = false;
    content.split(/\r?\n/).forEach((line) => {
        if (line.includes('===PROMO') && line.includes('===')) {
            inPromo = true;
            return;
        }
        if (line.startsWith('===') && !line.includes('===PROMO')) {
            inPromo = false;
            return;
        }
        if (inPromo && line.trim()) {
            promoWordCount += line.trim().split(/\s+/).length;
        }
    });

    const totalWords = original ? original.split(/\s+/).length : 0;
    const promoPercentage = cleanWordCount > 0 ? Math.round((promoWordCount / cleanWordCount) * 100) : 0;

    return {
        total_words: totalWords,
        clean_word_count: cleanWordCount,
        promo_word_count: promoWordCount,
        promo_percentage: promoPercentage,
        char_count: original.length
    };
};

function BlogWriter() {
    const { blogId } = useParams();
    const navigate = useNavigate();

    // State
    const [providers, setProviders] = useState(DEFAULT_AI_PROVIDERS);
    const [blogs, setBlogs] = useState([]);
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState('gemini');
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [generatedContent, setGeneratedContent] = useState(null);
    const [imageProgress, setImageProgress] = useState({ done: 0, total: 0, status: '' });
    const [imageUrls, setImageUrls] = useState({});
    const [realImageUrls, setRealImageUrls] = useState({});
    const imageObjectUrlsRef = useRef([]);
    const realImageObjectUrlsRef = useRef([]);
    const imageProgressIntervalRef = useRef(null);
    const imageProgressStartedAtRef = useRef(null);
    const [zoomImage, setZoomImage] = useState(null);
    const [zoomImagePrompt, setZoomImagePrompt] = useState('');
    const [realZoomImage, setRealZoomImage] = useState(null);
    const [selectedImageIdsForUpscale, setSelectedImageIdsForUpscale] = useState(() => new Set());
    const [upscaling, setUpscaling] = useState(false);
    const [realEditPrompts, setRealEditPrompts] = useState([]);
    const [realEditSelected, setRealEditSelected] = useState(() => new Set());
    const [realEditLoading, setRealEditLoading] = useState(false);
    const [realEditGenerating, setRealEditGenerating] = useState(false);
    const [realEditError, setRealEditError] = useState('');
    const [realEditCache, setRealEditCache] = useState({});
    const [realEditLoaded, setRealEditLoaded] = useState(false);
    const [imagesVersion, setImagesVersion] = useState(0);
    const [regeneratingImage, setRegeneratingImage] = useState(false);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewImages, setPreviewImages] = useState([]);
    const [previewPrompts, setPreviewPrompts] = useState([]);
    const [previewId, setPreviewId] = useState(null);
    const [previewStyle, setPreviewStyle] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewLoadingStyleKey, setPreviewLoadingStyleKey] = useState(null);
    const [previewError, setPreviewError] = useState(null);
    const [previewExpectedCount, setPreviewExpectedCount] = useState(4);
    const previewPollRef = useRef(null);

    const clearPreviewPolling = () => {
        if (previewPollRef.current) {
            clearInterval(previewPollRef.current);
            previewPollRef.current = null;
        }
    };
    const [styleChatMessages, setStyleChatMessages] = useState([]);
    const [styleChatInput, setStyleChatInput] = useState('');
    const [styleFeedbackLoading, setStyleFeedbackLoading] = useState(false);
    const [suggestedPrompt, setSuggestedPrompt] = useState('');
    const [applyingPrompt, setApplyingPrompt] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [showCreateBlogModal, setShowCreateBlogModal] = useState(false);
    const [newBlogName, setNewBlogName] = useState('');
    const [newBlogUrl, setNewBlogUrl] = useState('');
    const [creatingBlog, setCreatingBlog] = useState(false);

    // Prompt sections
    const [prompts, setPrompts] = useState({
        tone_of_voice: '',
        writing_style: '',
        emphasis_formatting: '',
        language_patterns: '',
        argumentation_style: ''
    });
    const [personaProfile, setPersonaProfile] = useState(PERSONA_TEMPLATE);
    const [personaLoading, setPersonaLoading] = useState(false);
    const [personaSaving, setPersonaSaving] = useState(false);
    const [referenceArticles, setReferenceArticles] = useState([]);

    // Generation settings
    const [promos, setPromos] = useState(['']);  // Array of promo texts

    // Research (Perplexity) settings
    const [researchSettings, setResearchSettings] = useState({
        perplexity_news_count: 12,
        perplexity_whitelist: [],
        perplexity_blacklist: [],
        perplexity_mode: 'default',
        detailed_mode: false,
        time_range_mode: 'preset',
        recency_preset: '7d',
        search_after_date: '',
        search_before_date: '',
        news_selection_mode: 'auto'
    });
    const [researchSettingsLoading, setResearchSettingsLoading] = useState(false);
    const [researchSettingsSaving, setResearchSettingsSaving] = useState(false);
    const [researchSettingsOpen, setResearchSettingsOpen] = useState(false);
    const [imageSettingsOpen, setImageSettingsOpen] = useState(false);
    const [selectedNewsIndices, setSelectedNewsIndices] = useState(() => new Set());
    const [confirmingNews, setConfirmingNews] = useState(false);

    // Promo field management
    const addPromoField = () => {
        if (promos.length >= 6) return; // Max 6 promos (CUST-02)
        setPromos([...promos, '']);
    };

    const removePromoField = (index) => {
        if (promos.length <= 1) return; // Keep at least 1
        setPromos(promos.filter((_, i) => i !== index));
        setPromoAttachments((prev) => {
            const next = {};
            Object.entries(prev || {}).forEach(([k, v]) => {
                const idx = Number(k);
                if (!Number.isInteger(idx)) return;
                if (idx < index) next[String(idx)] = v;
                else if (idx > index) next[String(idx - 1)] = v;
            });
            return next;
        });
    };

    const handleRegenerateImage = async () => {
        if (!zoomImage || !zoomImagePrompt) return;
        if (!zoomImage.id) {
            alert('Нельзя перегенерировать изображение без ID');
            return;
        }
        setRegeneratingImage(true);
        try {
            await generationAPI.regenerateImage(zoomImage.id, { prompt: zoomImagePrompt, aspect_ratio: aspectRatio });
            const refreshed = await generationAPI.getArticle(generatedContent.article.id);
            setGeneratedContent(refreshed.data);
            setImageUrls({});
            setZoomImage(null);
        } catch (error) {
            console.error('Failed to regenerate image', error);
            alert('Не удалось перегенерировать: ' + (error.response?.data?.detail || error.message));
        } finally {
            setRegeneratingImage(false);
        }
    };

    const toggleImageForUpscale = (img) => {
        if (!img?.id) return;
        setSelectedImageIdsForUpscale((prev) => {
            const next = new Set(prev);
            if (next.has(img.id)) next.delete(img.id);
            else next.add(img.id);
            return next;
        });
    };

    const handleUpscaleImages = async () => {
        if (!generatedContent?.article?.id || selectedImageIdsForUpscale.size === 0) return;
        setUpscaling(true);
        try {
            await generationAPI.upscaleImages(generatedContent.article.id, {
                image_ids: Array.from(selectedImageIdsForUpscale),
                aspect_ratio: aspectRatio,
            });
            const refreshed = await generationAPI.getArticle(generatedContent.article.id);
            setGeneratedContent(refreshed.data);
            setImageUrls({});
            setSelectedImageIdsForUpscale(new Set());
            setImagesVersion((v) => v + 1);
        } catch (error) {
            console.error('Upscale failed', error);
            alert('Upscale failed: ' + (error.response?.data?.detail || error.message));
        } finally {
            setUpscaling(false);
        }
    };
    const updateRealEditCache = (imageId, prompts, selectedSet, loaded = true) => {
        if (!imageId) return;
        const key = String(imageId);
        const normalizedPrompts = (prompts && prompts.length ? prompts : Array(5).fill('')).slice(0, 5);
        setRealEditCache((prev) => ({
            ...prev,
            [key]: {
                prompts: normalizedPrompts,
                selected: Array.from(selectedSet || []),
                loaded
            }
        }));
    };

    const sanitizeRealPrompts = (list) => {
        const filtered = (list || []).map((p) => (p || '').toString().trim())
            .map((p) => p.replace(/```json/gi, '').replace(/```/g, '').trim())
            .filter((p) => p)
            .filter((p) => !p.startsWith('{') && !p.startsWith('['))
            .filter((p) => !p.toLowerCase().includes('"prompts"'))
            .filter((p) => !p.toLowerCase().includes('```json'));
        return filtered;
    };

    const loadRealEditSuggestions = async (imageId, force = false) => {
        if (!imageId) return;
        const cacheKey = String(imageId);
        if (!force && realEditCache[cacheKey]?.loaded) {
            const cached = realEditCache[cacheKey];
            const cachedPrompts = (cached.prompts && cached.prompts.length ? cached.prompts : Array(5).fill('')).slice(0, 5);
            setRealEditPrompts(cachedPrompts);
            setRealEditSelected(new Set(cached.selected || []));
            setRealEditError('');
            setRealEditLoaded(true);
            return;
        }
        setRealEditLoading(true);
        setRealEditError('');
        try {
            const res = await generationAPI.getRealImageEditSuggestions(imageId);
            const prompts = Array.isArray(res?.data?.prompts) ? res.data.prompts : [];
            const normalized = sanitizeRealPrompts(prompts);
            const padded = [...normalized, ...Array(Math.max(0, 5 - normalized.length)).fill('')].slice(0, 5);
            setRealEditPrompts(padded);
            setRealEditSelected(new Set());
            setRealEditLoaded(true);
            updateRealEditCache(imageId, padded, new Set(), true);
        } catch (error) {
            console.error('Failed to load real photo edit suggestions', error);
            setRealEditError('Failed to load suggestions');
            setRealEditPrompts(['', '', '', '', '']);
            setRealEditSelected(new Set());
            setRealEditLoaded(false);
            updateRealEditCache(imageId, ['', '', '', '', ''], new Set(), false);
        } finally {
            setRealEditLoading(false);
        }
    };

    const handleRealEditGenerate = async () => {
        if (!realZoomImage?.id) return;
        const selected = realEditPrompts
            .map((p, idx) => (realEditSelected.has(idx) ? (p || '').trim() : ''))
            .filter((p) => p);
        if (selected.length === 0) {
            alert('Select at least one prompt');
            return;
        }
        setRealEditGenerating(true);
        try {
            await generationAPI.editRealImage(realZoomImage.id, { prompts: selected, aspect_ratio: aspectRatio });
            if (generatedContent?.article?.id) {
                const refreshed = await generationAPI.getArticle(generatedContent.article.id);
                setGeneratedContent(refreshed.data);
            }
            setImagesVersion((v) => v + 1);
        } catch (error) {
            console.error('Failed to edit real photo', error);
            alert('Failed to edit real photo: ' + (error.response?.data?.detail || error.message));
        } finally {
            setRealEditGenerating(false);
        }
    };

    const openRealImageZoom = (img, idx) => {
        const key = img?.id != null ? String(img.id) : `real-${idx}`;
        const src = realImageUrls[key] || '';
        const placementLabel = img?.block_key ? `After ${img.block_key}` : (img?.image_number <= 2 ? `Hero ${img.image_number}` : `#${img?.image_number || ''}`);
        const filename = img?.file_path ? img.file_path.split(/[/\\]/).pop() : '';
        setRealZoomImage({ id: img?.id, src, placementLabel, filename });
        if (img?.id) {
            const cached = realEditCache[String(img.id)];
            if (cached?.loaded) {
                const cachedPrompts = (cached.prompts && cached.prompts.length ? cached.prompts : Array(5).fill('')).slice(0, 5);
                setRealEditPrompts(cachedPrompts);
                setRealEditSelected(new Set(cached.selected || []));
                setRealEditLoaded(true);
            } else {
                setRealEditPrompts(Array(5).fill(''));
                setRealEditSelected(new Set());
                setRealEditLoaded(false);
            }
            setRealEditError('');
        }
    };

    const updatePromoField = (index, value) => {
        const newPromos = [...promos];
        newPromos[index] = value;
        setPromos(newPromos);
    };
    const [numBlocks, setNumBlocks] = useState(4);
    const [wordCount, setWordCount] = useState(1200);
    const [numImages, setNumImages] = useState(4);
    const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);
    const [numRealPhotos, setNumRealPhotos] = useState(0);
    const enableRealPhotos = import.meta.env.VITE_ENABLE_REAL_PHOTOS === 'true';
    const enableTopicPromoBlocks = import.meta.env.VITE_ENABLE_TOPIC_PROMO_BLOCKS === 'true';
    const enableDetailedPerplexity = import.meta.env.VITE_ENABLE_DETAILED_PERPLEXITY === 'true';
    const [articleTopic, setArticleTopic] = useState('');
    const [articleDate, setArticleDate] = useState(null);
    const [promoAttachments, setPromoAttachments] = useState({});
    const [blockSettings, setBlockSettings] = useState({});
    const [blockSettingsOpen, setBlockSettingsOpen] = useState({});

    // Article structure configuration
    const [articleStructure, setArticleStructure] = useState([]);
    const [structureTouched, setStructureTouched] = useState(false);

    const buildDefaultStructure = useCallback(() => {
        const structure = ['intro'];
        const contentBlocks = Array.from({ length: numBlocks }, () => 'content');
        structure.push(...contentBlocks);

        // Distribute promo markers across the structure so users can reorder or remove them.
        const promoCount = Math.max(1, promos.length || 1);
        const gap = Math.max(1, Math.floor(structure.length / (promoCount + 1)));
        let insertAt = gap;
        for (let i = 0; i < promoCount; i++) {
            if (insertAt > structure.length) {
                insertAt = structure.length;
            }
            structure.splice(insertAt, 0, 'promo');
            insertAt += gap + 1;
        }

        return structure;
    }, [numBlocks, promos.length]);

    // Initialize structure when numBlocks changes or promo count changes (unless user already tweaked it)
    useEffect(() => {
        if (!structureTouched || articleStructure.length === 0) {
            setArticleStructure(buildDefaultStructure());
            return;
        }

        // When user already touched the structure, make sure content block count matches selection.
        const currentContentCount = articleStructure.filter(item => item === 'content').length;
        const targetContentCount = numBlocks;
        let updated = [...articleStructure];

        if (currentContentCount < targetContentCount) {
            const missing = targetContentCount - currentContentCount;
            updated = [...updated, ...Array.from({ length: missing }, () => 'content')];
        } else if (currentContentCount > targetContentCount) {
            // Remove extra content blocks from the end (keep intro/promos intact)
            let toRemove = currentContentCount - targetContentCount;
            for (let i = updated.length - 1; i >= 0 && toRemove > 0; i--) {
                if (updated[i] === 'content') {
                    updated.splice(i, 1);
                    toRemove -= 1;
                }
            }
        }

        if (updated.length !== articleStructure.length) {
            setArticleStructure(updated);
        }
    }, [buildDefaultStructure, structureTouched, articleStructure, numBlocks]);

    const resetStructure = () => {
        setStructureTouched(false);
        setArticleStructure(buildDefaultStructure());
    };

    const moveBlock = (index, direction) => {
        setStructureTouched(true);
        const hasIntro = articleStructure[0] === 'intro';
        if (hasIntro && index === 0) return; // Can't move intro
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        const minIndex = hasIntro ? 1 : 0;
        if (newIndex < minIndex || newIndex >= articleStructure.length) return; // Keep intro first if present

        const newStructure = [...articleStructure];
        [newStructure[index], newStructure[newIndex]] = [newStructure[newIndex], newStructure[index]];
        setArticleStructure(newStructure);
    };

    const removeIntro = () => {
        setStructureTouched(true);
        setArticleStructure(articleStructure.filter((item) => item !== 'intro'));
    };

    const insertPromo = (afterIndex) => {
        setStructureTouched(true);
        const newStructure = [...articleStructure];
        newStructure.splice(afterIndex + 1, 0, 'promo');
        setArticleStructure(newStructure);
    };

    const removePromo = (index) => {
        setStructureTouched(true);
        setArticleStructure(articleStructure.filter((_, i) => i !== index));
    };

    const contentBlockNumbers = useMemo(() => {
        const out = [];
        let idx = 0;
        for (const item of articleStructure) {
            if ((item || '').toLowerCase() === 'content') {
                idx += 1;
                out.push(idx);
            }
        }
        return out;
    }, [articleStructure]);

    useEffect(() => {
        const validSet = new Set(contentBlockNumbers);
        setPromoAttachments((prev) => {
            const next = {};
            Object.entries(prev || {}).forEach(([promoIdx, blockIdx]) => {
                const parsed = Number(blockIdx);
                if (Number.isInteger(parsed) && validSet.has(parsed)) {
                    next[promoIdx] = parsed;
                }
            });
            return next;
        });
        setBlockSettings((prev) => {
            const next = {};
            Object.entries(prev || {}).forEach(([blockIdx, cfg]) => {
                const parsed = Number(blockIdx);
                if (Number.isInteger(parsed) && validSet.has(parsed)) {
                    next[String(parsed)] = cfg;
                }
            });
            return next;
        });
        setBlockSettingsOpen((prev) => {
            const next = {};
            Object.entries(prev || {}).forEach(([blockIdx, isOpen]) => {
                const parsed = Number(blockIdx);
                if (Number.isInteger(parsed) && validSet.has(parsed) && isOpen) {
                    next[String(parsed)] = true;
                }
            });
            return next;
        });
    }, [contentBlockNumbers]);

    const setPromoAttachment = (promoIndex, blockIndexOrNull) => {
        setPromoAttachments((prev) => {
            const next = { ...(prev || {}) };
            if (blockIndexOrNull == null || blockIndexOrNull === '') delete next[String(promoIndex)];
            else next[String(promoIndex)] = Number(blockIndexOrNull);
            return next;
        });
    };

    const updateBlockSetting = (blockIndex, patch) => {
        const key = String(blockIndex);
        setBlockSettings((prev) => {
            const current = { ...(prev?.[key] || {}) };
            const nextEntry = { ...current, ...patch };
            if (nextEntry.word_count === '' || nextEntry.word_count == null) delete nextEntry.word_count;
            if (!nextEntry.focus || !String(nextEntry.focus).trim()) delete nextEntry.focus;
            const next = { ...(prev || {}) };
            if (Object.keys(nextEntry).length === 0) delete next[key];
            else next[key] = nextEntry;
            return next;
        });
    };

    const toggleBlockSettingsPanel = (blockIndex) => {
        const key = String(blockIndex);
        setBlockSettingsOpen((prev) => ({ ...prev, [key]: !prev?.[key] }));
    };


    // Image styles - toggle on/off by clicking cards
    const [imageStyles, setImageStyles] = useState(DEFAULT_IMAGE_STYLES);

    // Style editing modal
    const [styleModalOpen, setStyleModalOpen] = useState(false);
    const [editingStyle, setEditingStyle] = useState(null);
    const [styleForm, setStyleForm] = useState({ name: '', short_name: '', description: '', prompt: '' });

    // Template management
    const [templates, setTemplates] = useState({});  // { section_type: [templates] }
    const [templateModalOpen, setTemplateModalOpen] = useState(false);
    const [templateAction, setTemplateAction] = useState('save');  // 'save' or 'load'
    const [templateSection, setTemplateSection] = useState('');  // Which section we're saving/loading
    const [templateName, setTemplateName] = useState('');
    const [templateLoading, setTemplateLoading] = useState(false);
    const [deletingBlogId, setDeletingBlogId] = useState(null);

    // Step tracking
    const [currentStep, setCurrentStep] = useState(1);
    const [articleStatusHistory, setArticleStatusHistory] = useState([]);
    const [articleStatusInfo, setArticleStatusInfo] = useState(null);
    const [articleError, setArticleError] = useState(null);
    const [articlePolling, setArticlePolling] = useState(false);
    const [gameActive, setGameActive] = useState(false);

    const submitGameScore = useCallback(async (finalScore, metadata = {}) => {
        if (!finalScore || finalScore <= 0) return;
        try {
            await api.post('/api/game/scores', {
                game_type: 'tetris',
                score: finalScore,
                level: metadata.level || null,
                lines_cleared: metadata.lines || null,
            });
        } catch {}
    }, []);

    const pollingTimeoutRef = useRef(null);
    const generationStartedAtRef = useRef(null);
    const generationFinalizedRef = useRef(false);

    useEffect(() => {
        const init = async () => {
            try {
                const response = await generationAPI.getProviders();
                const availability = response.data || {};
                setProviders(prev => prev.map(p => ({
                    ...p,
                    available: availability[p.id] !== undefined ? availability[p.id] : p.available
                })));
            } catch (error) {
                console.error('Failed to load providers:', error);
            }
            loadBlogs();
        };
        init();
    }, []);

    useEffect(() => {
        if (blogId && blogs.length > 0) {
            const blog = blogs.find(b => b.id === blogId);
            if (blog && blog.id !== selectedBlog?.id) {
                selectBlog(blog);
            }
        }
    }, [blogId, blogs, selectedBlog?.id]);

    const blockSettingsSyncedBlogIdRef = useRef(null);
    useEffect(() => {
        if (selectedBlog?.id != null) blockSettingsSyncedBlogIdRef.current = null;
    }, [selectedBlog?.id]);

    // Hydrate generation_settings when blog changes (PERSIST-01)
    const hydratedBlogIdRef = useRef(null);
    useEffect(() => {
        if (!selectedBlog) return;
        if (hydratedBlogIdRef.current === selectedBlog.id) return;
        hydratedBlogIdRef.current = selectedBlog.id;
        const gs = selectedBlog.generation_settings;
        if (gs && typeof gs === 'object' && Object.keys(gs).length > 0) {
            if (typeof gs.num_blocks === 'number') setNumBlocks(gs.num_blocks);
            if (typeof gs.word_count_target === 'number') setWordCount(gs.word_count_target);
            if (typeof gs.num_images === 'number') setNumImages(gs.num_images);
            if (typeof gs.num_real_photos === 'number') setNumRealPhotos(gs.num_real_photos);
            if (typeof gs.ai_provider === 'string') setSelectedProvider(gs.ai_provider);
            if (typeof gs.aspect_ratio === 'string') setAspectRatio(gs.aspect_ratio);
            if (gs.promo_attachments && typeof gs.promo_attachments === 'object') {
                setPromoAttachments(gs.promo_attachments);
            }
            if (gs.research_settings && typeof gs.research_settings === 'object' && Object.keys(gs.research_settings).length > 0) {
                setResearchSettings((s) => ({ ...s, ...gs.research_settings }));
            }
            if (Array.isArray(gs.article_structure) && gs.article_structure.length > 0) {
                const saved = gs.article_structure;
                const savedPromoCount = saved.filter(i => i === 'promo').length;
                const neededPromoCount = Math.max(1, (promos || []).length || 1);
                if (neededPromoCount > savedPromoCount) {
                    const extra = neededPromoCount - savedPromoCount;
                    const merged = [...saved];
                    for (let i = 0; i < extra; i++) merged.push('promo');
                    setArticleStructure(merged);
                } else {
                    setArticleStructure(saved);
                }
                setStructureTouched(true);
            }
            if (gs.block_settings && typeof gs.block_settings === 'object' && Object.keys(gs.block_settings).length > 0) {
                blockSettingsSyncedBlogIdRef.current = selectedBlog.id;
                setBlockSettings(gs.block_settings);
                return;
            }
        }
        // No saved generation_settings (or no block_settings within it) — build default equal split
        const numContent = articleStructure.filter(i => i === 'content').length;
        if (numContent === 0) return;
        if (blockSettingsSyncedBlogIdRef.current === selectedBlog.id) return;
        blockSettingsSyncedBlogIdRef.current = selectedBlog.id;
        const per = Math.round(wordCount / Math.max(1, numContent));
        const clamped = Math.min(5000, Math.max(50, per));
        const next = {};
        for (let i = 1; i <= numContent; i++) next[String(i)] = clamped != null ? { word_count: clamped } : {};
        setBlockSettings(next);
    }, [selectedBlog?.id, articleStructure.length]);

    // --- Ref-based auto-save: interval polls refs every 3s, saves if changed ---
    const settingsRef = useRef(null);
    const lastSavedJsonRef = useRef(null);
    const blogIdForSaveRef = useRef(null);
    const [settingsDirty, setSettingsDirty] = useState(false);

    // Keep refs always in sync with latest state
    useEffect(() => {
        blogIdForSaveRef.current = selectedBlog?.id || null;
    }, [selectedBlog?.id]);
    useEffect(() => {
        settingsRef.current = {
            num_blocks: numBlocks,
            word_count_target: wordCount,
            num_images: numImages,
            num_real_photos: numRealPhotos,
            num_headers: 4,
            ai_provider: selectedProvider,
            promo_attachments: promoAttachments,
            block_settings: blockSettings,
            research_settings: researchSettings,
            article_structure: articleStructure,
            aspect_ratio: aspectRatio,
        };
    }, [numBlocks, wordCount, numImages, numRealPhotos, selectedProvider, promoAttachments, blockSettings, researchSettings, articleStructure, aspectRatio]);

    // Snapshot after hydration completes so we don't save unchanged settings
    useEffect(() => {
        if (!selectedBlog?.id) return;
        const t = setTimeout(() => {
            if (settingsRef.current) {
                lastSavedJsonRef.current = JSON.stringify(settingsRef.current);
            }
        }, 1200);
        return () => clearTimeout(t);
    }, [selectedBlog?.id]);

    const doSaveSettings = useCallback(() => {
        const blogId = blogIdForSaveRef.current;
        const gs = settingsRef.current;
        if (!blogId || !gs) return;
        const json = JSON.stringify(gs);
        if (json === lastSavedJsonRef.current) return;
        lastSavedJsonRef.current = json;
        setSettingsDirty(false);
        api.patch(`/api/blogs/${blogId}`, { generation_settings: gs }).catch((err) => {
            console.error('Failed to save generation settings:', err);
        });
    }, []);

    // Interval: check every 3s and save if dirty
    useEffect(() => {
        const id = setInterval(() => {
            const blogId = blogIdForSaveRef.current;
            const gs = settingsRef.current;
            if (!blogId || !gs) return;
            const json = JSON.stringify(gs);
            if (json !== lastSavedJsonRef.current) {
                setSettingsDirty(true);
                lastSavedJsonRef.current = json;
                api.patch(`/api/blogs/${blogId}`, { generation_settings: gs }).then(() => {
                    setSettingsDirty(false);
                }).catch((err) => {
                    console.error('Failed to save generation settings:', err);
                });
            }
        }, 3000);
        return () => clearInterval(id);
    }, []);

    // Save on unmount / blog change
    useEffect(() => {
        return () => doSaveSettings();
    }, [selectedBlog?.id, doSaveSettings]);


    useEffect(() => {
        if (zoomImage) {
            setZoomImagePrompt(zoomImage.prompt || '');
        } else {
            setZoomImagePrompt('');
        }
    }, [zoomImage]);

    useEffect(() => {
        if (!realZoomImage) {
            setRealEditError('');
            setRealEditLoading(false);
            setRealEditGenerating(false);
            setRealEditLoaded(false);
        }
    }, [realZoomImage]);

    useEffect(() => {
        setSelectedImageIdsForUpscale(new Set());
        setRealZoomImage(null);
    }, [generatedContent?.article?.id]);

    useEffect(() => {
        return () => {
            stopAllPolling();
            clearPreviewPolling();
            clearImageObjectUrls();
            realImageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            realImageObjectUrlsRef.current = [];
        };
    }, []);

    useEffect(() => {
        const fetchImagePreviews = async () => {
            clearImageObjectUrls();
            const images = generatedContent?.images || [];
            if (!images.length) {
                setImageUrls({});
                return;
            }

            const cacheBuster = Date.now();
            const entries = await Promise.all(
                images.map(async (img, idx) => {
                    const key = img.id || `image-${idx}`;
                    if (!img.id) {
                        return [key, null];
                    }
                    const base = `/api/generate/images/${img.id}/download`;
                    const endpoint = base + (base.includes('?') ? '&' : '?') + 'v=' + cacheBuster;

                    try {
                        const response = await api.get(endpoint, { responseType: 'blob' });
                        const blobUrl = URL.createObjectURL(response.data);
                        imageObjectUrlsRef.current.push(blobUrl);
                        return [key, blobUrl];
                    } catch (error) {
                        console.error('Failed to load image preview', error);
                        return [key, null];
                    }
                })
            );

            setImageUrls(Object.fromEntries(entries));
        };

        fetchImagePreviews();

        return () => {
            clearImageObjectUrls();
        };
    }, [generatedContent?.images, imagesVersion]);

    const realImagesList = generatedContent?.real_images ?? generatedContent?.realImages ?? [];
    const realPhotoProgress = generatedContent?.real_photo_progress || null;
    const formatDuration = (seconds) => {
        if (seconds == null || Number.isNaN(Number(seconds))) return '—';
        const s = Math.max(0, Math.round(Number(seconds)));
        if (s < 60) return `${s}s`;
        const mins = Math.floor(s / 60);
        const rem = s % 60;
        return `${mins}m ${rem}s`;
    };
    useEffect(() => {
        const fetchRealImagePreviews = async () => {
            realImageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            realImageObjectUrlsRef.current = [];
            const images = realImagesList;
            if (!images.length) {
                setRealImageUrls({});
                return;
            }
            const cacheBuster = Date.now();
            const entries = await Promise.all(
                images.map(async (img, idx) => {
                    const id = img.id;
                    const key = id != null ? String(id) : `real-${idx}`;
                    if (!id) return [key, null];
                    const endpoint = `/api/generate/real-images/${id}/download?v=${cacheBuster}`;
                    try {
                        const response = await api.get(endpoint, { responseType: 'blob' });
                        const blobUrl = URL.createObjectURL(response.data);
                        realImageObjectUrlsRef.current.push(blobUrl);
                        return [key, blobUrl];
                    } catch (err) {
                        console.error('Failed to load real image preview', err);
                        return [key, null];
                    }
                })
            );
            setRealImageUrls(Object.fromEntries(entries));
        };
        fetchRealImagePreviews();
        return () => {
            realImageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
            realImageObjectUrlsRef.current = [];
        };
    }, [generatedContent?.real_images, generatedContent?.realImages, imagesVersion]);

    useEffect(() => {
        if (realZoomImage?.id != null) {
            const key = String(realZoomImage.id);
            const src = realImageUrls[key];
            if (src && src !== realZoomImage.src) {
                setRealZoomImage((prev) => ({ ...prev, src }));
            }
        }
    }, [realImageUrls, realZoomImage?.id]);

    // When on step 4 with article but no real_images yet (e.g. real_photos still saving), refetch
    useEffect(() => {
        if (currentStep !== 4 || !generatedContent?.article?.id || numRealPhotos <= 0 || realImagesList.length > 0) return;
        const aid = generatedContent.article.id;
        const t1 = setTimeout(() => {
            generationAPI.getArticle(aid).then((r) => r.data && setGeneratedContent(r.data)).catch(() => {});
        }, 3000);
        const t2 = setTimeout(() => {
            generationAPI.getArticle(aid).then((r) => r.data && setGeneratedContent(r.data)).catch(() => {});
        }, 7000);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [currentStep, generatedContent?.article?.id, numRealPhotos, realImagesList.length]);

    useEffect(() => {
        if (currentStep !== 2 && currentStep !== 3) return;
        setResearchSettingsLoading(true);
        authAPI.getResearchSettings()
            .then((r) => {
                try {
                    const data = r?.data;
                    if (data == null || typeof data !== 'object') return;
                    setResearchSettings((s) => ({
                        ...s,
                        perplexity_news_count: typeof data.perplexity_news_count === 'number' ? data.perplexity_news_count : (s.perplexity_news_count ?? 12),
                        perplexity_whitelist: Array.isArray(data.perplexity_whitelist) ? data.perplexity_whitelist : (s.perplexity_whitelist || []),
                        perplexity_blacklist: Array.isArray(data.perplexity_blacklist) ? data.perplexity_blacklist : (s.perplexity_blacklist || []),
                        perplexity_mode: _normalizePerplexityModeForState(data.perplexity_mode ?? s.perplexity_mode),
                        detailed_mode: typeof data.detailed_mode === 'boolean' ? data.detailed_mode : !!s.detailed_mode,
                        time_range_mode: typeof data.time_range_mode === 'string' ? data.time_range_mode : (s.time_range_mode || 'preset'),
                        recency_preset: typeof data.recency_preset === 'string' ? data.recency_preset : (s.recency_preset || '7d'),
                        search_after_date: typeof data.search_after_date === 'string' ? data.search_after_date : (s.search_after_date || ''),
                        search_before_date: typeof data.search_before_date === 'string' ? data.search_before_date : (s.search_before_date || ''),
                        news_selection_mode: typeof data.news_selection_mode === 'string' ? data.news_selection_mode : (s.news_selection_mode || 'auto')
                    }));
                } catch (_) { /* ignore */ }
            })
            .catch(() => {})
            .finally(() => setResearchSettingsLoading(false));
    }, [currentStep]);

    useEffect(() => {
        if (currentStep === 4 && generatedContent?.article?.status === 'awaiting_news_selection' && (generatedContent.article?.news_candidates?.length ?? 0) > 0) {
            setSelectedNewsIndices(new Set());
        }
    }, [currentStep, generatedContent?.article?.status, generatedContent?.article?.news_candidates?.length]);

    const loadBlogs = async () => {
        try {
            const response = await blogsAPI.getAll();
            setBlogs(response.data);
        } catch (error) {
            console.error('Failed to load blogs:', error);
        } finally {
            setLoading(false);
        }
    };

    const clearImageObjectUrls = () => {
        imageObjectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        imageObjectUrlsRef.current = [];
    };

    const handleDownloadImage = async (img, idx) => {
        if (!img.id) {
            alert('Image not available for download');
            return;
        }
        const endpoint = `/api/generate/images/${img.id}/download`;

        try {
            const response = await api.get(endpoint, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            const filenameBase = img.filename || img.file_path?.split('/').pop() || `image_${idx + 1}.png`;
            link.download = filenameBase;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download image', error);
            alert('Failed to download image. Please try again.');
        }
    };

    const handleDownloadRealImage = async (img, idx) => {
        if (!img.id) {
            alert('Image not available for download');
            return;
        }
        const endpoint = `/api/generate/real-images/${img.id}/download`;
        try {
            const response = await api.get(endpoint, { responseType: 'blob' });
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            const filenameBase = img.file_path?.split(/[/\\]/).pop() || `real_${idx + 1}.png`;
            link.download = filenameBase;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download real image', error);
            alert('Failed to download real image. Please try again.');
        }
    };

    const handleDownloadAllImages = async () => {
        if (!generatedContent?.article?.id) return;
        setDownloadingAll(true);
        try {
            const response = await api.get(
                `/api/generate/articles/${generatedContent.article.id}/images/download-all`,
                { responseType: 'blob' }
            );
            const url = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${(generatedContent.article.title || 'images').replace(/\\s+/g, '_')}.zip`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to download all images', error);
            alert('Failed to download all images. Please try again.');
        } finally {
            setDownloadingAll(false);
        }
    };

    const handleCreateBlog = async () => {
        const trimmedName = newBlogName.trim();
        if (!trimmedName || creatingBlog) {
            return;
        }

        setCreatingBlog(true);
        try {
            const payload = {
                name: trimmedName,
                url: newBlogUrl.trim() || null,
                persona_name: trimmedName
            };
            const response = await blogsAPI.create(payload);
            const createdBlog = response.data;
            setBlogs(prev => [...prev, createdBlog]);
            setNewBlogName('');
            setNewBlogUrl('');
            setShowCreateBlogModal(false);
            await selectBlog(createdBlog);
            setPromos(['']);
            setGeneratedContent(null);
            setArticleStatusHistory([]);
            setArticleStatusInfo(null);
            setArticleError(null);
            setArticlePolling(false);
            setCurrentStep(2);
        } catch (error) {
            console.error('Failed to create blog:', error);
            alert('Failed to create blog: ' + (error.response?.data?.detail || error.message));
        } finally {
            setCreatingBlog(false);
        }
    };

    const loadImageStyles = async (blogId) => {
        try {
            const response = await blogsAPI.getImageStyles(blogId);
            if (response.data && response.data.length > 0) {
                // Add icons to loaded styles
                const stylesWithIcons = response.data.map(decorateStyle);
                setImageStyles(stylesWithIcons);
            } else {
                setImageStyles(DEFAULT_IMAGE_STYLES);
            }
        } catch (error) {
            console.error('Failed to load image styles:', error);
            setImageStyles(DEFAULT_IMAGE_STYLES);
        }
    };

    const startPreviewPolling = (blogId, styleId, previewId, targetCount = 4) => {
        clearPreviewPolling();
        const maxAttempts = 100; // ~250s at 2.5s interval
        let attempts = 0;
        const poll = async () => {
            try {
                attempts += 1;
                const res = await blogsAPI.getStylePreview(blogId, styleId, previewId);
                const imgs = (res.data.images || []).map((img, idx) => ({
                    src: `/api/blogs/${blogId}/image-styles/${styleId}/previews/${previewId}/images/${idx}`,
                    prompt: img.prompt,
                    index: idx
                }));
                setPreviewImages(imgs);
                setPreviewPrompts(res.data.prompts || []);
                const isComplete = imgs.length >= targetCount;
                const timedOut = attempts >= maxAttempts;
                setPreviewLoading(!isComplete);
                if (isComplete || timedOut) {
                    if (timedOut && !isComplete) {
                        setPreviewError('Preview generation timed out. Try again or reduce preview size.');
                        setPreviewLoading(false);
                    }
                    clearPreviewPolling();
                }
            } catch (error) {
                console.error('Failed to load image preview', error);
                setPreviewError(error.response?.data?.detail || error.message);
                setPreviewLoading(false);
                clearPreviewPolling();
            }
        };
        poll();
        previewPollRef.current = setInterval(poll, 2500);
    };

    const openStylePreview = async (style) => {
        if (!selectedBlog) return;
        clearPreviewPolling();
        const targetCount = 4;
        setPreviewStyle(style);
        setPreviewImages([]);
        setPreviewPrompts([]);
        setPreviewId(null);
        setPreviewError(null);
        setSuggestedPrompt('');
        setStyleChatMessages([]);
        setStyleChatInput('');
        setStyleFeedbackLoading(false);
        setPreviewExpectedCount(targetCount);
        setPreviewModalOpen(true);
        setPreviewLoadingStyleKey(style.short_name || style.id);
        setPreviewLoading(true);
        try {
            const res = await blogsAPI.previewStyle(selectedBlog.id, style.id);
            const newPreviewId = res.data.id;
            setPreviewId(newPreviewId);
            const imgs = (res.data.images || []).map((img, idx) => ({
                src: `/api/blogs/${selectedBlog.id}/image-styles/${style.id}/previews/${newPreviewId}/images/${idx}`,
                prompt: img.prompt,
                index: idx
            }));
            setPreviewImages(imgs);
            setPreviewPrompts(res.data.prompts || []);
            const isComplete = imgs.length >= targetCount;
            setPreviewLoading(!isComplete);
            startPreviewPolling(selectedBlog.id, style.id, newPreviewId, targetCount);
        } catch (error) {
            console.error('Failed to load image preview', error);
            setPreviewError(error.response?.data?.detail || error.message);
            setPreviewLoading(false);
            clearPreviewPolling();
        } finally {
            setPreviewLoadingStyleKey(null);
        }
    };

    const sendStyleFeedback = async () => {
        if (!previewStyle || !styleChatInput || !selectedBlog) return;
        if (styleFeedbackLoading) return;
        const msg = styleChatInput;
        setStyleChatMessages(prev => [...prev, { role: 'user', content: msg }]);
        setStyleChatInput('');
        setStyleFeedbackLoading(true);
        try {
            const res = await blogsAPI.styleFeedback(selectedBlog.id, previewStyle.id, { message: msg });
            const newPrompt = res.data.suggested_prompt || '';
            const cleanedPrompt = stripUserEcho(newPrompt, msg);
            setSuggestedPrompt(newPrompt);
            setStyleChatMessages(prev => [...prev, { role: 'assistant', content: cleanedPrompt }]);
        } catch (error) {
            console.error('Style feedback failed', error);
            setStyleChatMessages(prev => [...prev, { role: 'assistant', content: 'Ошибка: не удалось переписать стиль' }]);
        } finally {
            setStyleFeedbackLoading(false);
        }
    };

    const applySuggestedPrompt = async () => {
        if (!previewStyle || !selectedBlog || !suggestedPrompt) return;
        setApplyingPrompt(true);
        try {
            await blogsAPI.applyStyle(selectedBlog.id, previewStyle.id, { prompt: suggestedPrompt });
            await loadImageStyles(selectedBlog.id);
        } catch (error) {
            console.error('Apply style failed', error);
            alert('Не удалось применить стиль: ' + (error.response?.data?.detail || error.message));
        } finally {
            setApplyingPrompt(false);
        }
    };

    const closePreviewModal = () => {
        clearPreviewPolling();
        setPreviewLoading(false);
        setPreviewModalOpen(false);
    };

    const loadPersona = async (blogId) => {
        setPersonaLoading(true);
        try {
            const response = await blogsAPI.getPersona(blogId);
            const { reference_articles = [], ...profile } = response.data;
            setPersonaProfile({ ...PERSONA_TEMPLATE, ...profile });
            setReferenceArticles(reference_articles || []);
        } catch (error) {
            console.error('Failed to load persona profile:', error);
            setPersonaProfile(PERSONA_TEMPLATE);
            setReferenceArticles([]);
        } finally {
            setPersonaLoading(false);
        }
    };

    const addReferenceArticle = () => {
        setReferenceArticles((prev) => [...prev, { title: '', summary: '' }]);
    };

    const updateReferenceArticle = (index, field, value) => {
        setReferenceArticles((prev) =>
            prev.map((item, idx) => (idx === index ? { ...item, [field]: value } : item))
        );
    };

    const removeReferenceArticle = (index) => {
        setReferenceArticles((prev) => prev.filter((_, idx) => idx !== index));
    };

    const savePersona = async () => {
        if (!selectedBlog) return;
        setPersonaSaving(true);
        try {
            const payload = { ...personaProfile, reference_articles: referenceArticles };
            await blogsAPI.updatePersona(selectedBlog.id, payload);
        } catch (error) {
            console.error('Failed to save persona profile:', error);
        } finally {
            setPersonaSaving(false);
        }
    };

    const handleDeletePersona = async () => {
        if (!selectedBlog) return;
        if (!confirm('Delete this persona and reset it to the default profile?')) return;
        try {
            await blogsAPI.deletePersona(selectedBlog.id);
            await loadPersona(selectedBlog.id);
        } catch (error) {
            console.error('Failed to delete persona profile:', error);
            alert('Failed to delete persona');
        }
    };

    const selectBlog = async (blog) => {
        setSelectedBlog(blog);
        navigate(`/blog-writer/${blog.id}`);

        try {
            const response = await blogsAPI.getPrompts(blog.id);
            setPrompts({
                tone_of_voice: response.data.tone_of_voice || '',
                writing_style: response.data.writing_style || '',
                emphasis_formatting: response.data.emphasis_formatting || '',
                language_patterns: response.data.language_patterns || '',
                argumentation_style: response.data.argumentation_style || ''
            });

            // Load custom image styles for this blog
            await loadPersona(blog.id);
            await loadImageStyles(blog.id);
        } catch (error) {
            console.error('Failed to load prompts:', error);
        }
    };

    const deleteBlog = async (blog) => {
        if (!blog) return;
        if (!confirm(`Delete blog "${blog.name}"? This cannot be undone.`)) return;
        try {
            setDeletingBlogId(blog.id);
            await blogsAPI.delete(blog.id);
            // Refresh the list from server to ensure UI matches DB
            await loadBlogs();

            setBlogs((prev) => prev.filter((b) => b.id !== blog.id));
            if (selectedBlog?.id === blog.id) {
                setSelectedBlog(null);
                setCurrentStep(1);
                navigate('/blog-writer');
            }
            setGeneratedContent(null);
        } catch (error) {
            console.error('Failed to delete blog:', error);
            alert('Failed to delete blog: ' + (error.response?.data?.detail || error.message));
        } finally {
            setDeletingBlogId(null);
        }
    };

    const toggleStyleActive = async (style) => {
        if (!style) return;
        const isEnabled = style.is_enabled !== false;
        const enabledCount = imageStyles.filter(s => s.is_enabled !== false).length;
        const nextEnabled = !isEnabled;
        if (!nextEnabled && enabledCount <= 1) return;

        const matchStyle = (entry) =>
            (style.id && entry.id === style.id) || (style.short_name && entry.short_name === style.short_name);

        setImageStyles(prev => prev.map(s => (matchStyle(s) ? { ...s, is_enabled: nextEnabled } : s)));

        if (!selectedBlog || !style.id || style.id.length !== 36) return;
        try {
            await blogsAPI.updateImageStyle(selectedBlog.id, style.id, { is_enabled: nextEnabled });
        } catch (error) {
            console.error('Failed to toggle style:', error);
            setImageStyles(prev => prev.map(s => (matchStyle(s) ? { ...s, is_enabled: isEnabled } : s)));
        }
    };

    // Open style edit/create modal
    const openStyleModal = (style = null) => {
        setEditingStyle(style);
        setStyleForm({
            name: style?.name || '',
            short_name: style?.short_name || '',
            description: style?.description || '',
            prompt: style?.prompt || ''
        });
        setStyleModalOpen(true);
    };

    const closeStyleModal = () => {
        setStyleModalOpen(false);
        setEditingStyle(null);
        setStyleForm({ name: '', short_name: '', description: '', prompt: '' });
    };

    // Save style changes
    const saveStyleChanges = async () => {
        if (!selectedBlog) return;

        try {
            if (editingStyle) {
                const payload = {
                    name: styleForm.name,
                    description: styleForm.description,
                    prompt: styleForm.prompt
                };
                await blogsAPI.updateImageStyle(selectedBlog.id, editingStyle.id, payload);

                setImageStyles(prev => prev.map(s =>
                    s.id === editingStyle.id ? { ...s, ...payload } : s
                ));
            } else {
                const normalizedShortName = sanitizeShortName(styleForm.short_name || styleForm.name);
                if (!styleForm.name.trim() || !styleForm.prompt.trim() || !normalizedShortName) {
                    alert('Please provide name, short name, and prompt for the style');
                    return;
                }
                if (imageStyles.some(s => s.short_name === normalizedShortName)) {
                    alert('Short name already in use. Please choose another one.');
                    return;
                }

                const payload = {
                    name: styleForm.name.trim(),
                    short_name: normalizedShortName,
                    description: styleForm.description,
                    prompt: styleForm.prompt,
                    is_enabled: true
                };

                const response = await blogsAPI.createImageStyle(selectedBlog.id, payload);
                const newStyle = decorateStyle(response.data);
                setImageStyles(prev => [...prev, newStyle]);
            }

            closeStyleModal();
        } catch (error) {
            console.error('Failed to save style:', error);
            alert('Failed to save style changes');
        }
    };

    const deleteStyle = async (style) => {
        if (!selectedBlog || style.is_default || !style.id || style.id.length !== 36) return;
        if (!confirm('Delete this style?')) return;

        try {
            await blogsAPI.deleteImageStyle(selectedBlog.id, style.id);
            const remainingStyles = imageStyles.filter(s => s.id !== style.id);
            setImageStyles(remainingStyles);
        } catch (error) {
            console.error('Failed to delete style:', error);
            alert('Failed to delete style');
        }
    };

    const stopImageProgressPolling = () => {
        if (imageProgressIntervalRef.current) {
            clearInterval(imageProgressIntervalRef.current);
            imageProgressIntervalRef.current = null;
        }
    };

    const stopAllPolling = () => {
        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }
        stopImageProgressPolling();
    };

    const getGenerationElapsedMs = () => {
        if (!generationStartedAtRef.current) return 0;
        return Date.now() - generationStartedAtRef.current;
    };

    const resetImageProgress = () => {
        stopImageProgressPolling();
        setImageProgress({ done: 0, total: 0, status: '' });
    };

    const startImageProgressPolling = (articleId, totalImages) => {
        const targetTotal = totalImages ?? numImages ?? 0;
        if (!articleId) return;
        if (targetTotal <= 0 && numRealPhotos <= 0) return;

        stopImageProgressPolling();
        imageProgressStartedAtRef.current = Date.now();

        const poll = async () => {
            if (generationFinalizedRef.current) {
                stopImageProgressPolling();
                return;
            }
            try {
                const response = await generationAPI.getArticle(articleId);
                const { article, images, real_images, real_photo_progress, stage_timings } = response.data;
                const doneCount = Array.isArray(images) ? images.length : 0;
                setImageProgress({
                    done: doneCount,
                    total: targetTotal,
                    status: article?.status_message || ''
                });
                // If backend now has more images or real_images than the current payload, refresh the UI state.
                setGeneratedContent((prev) => {
                    const prevCount = prev?.images?.length || 0;
                    const prevRealCount = prev?.real_images?.length || 0;
                    const prevArticleId = prev?.article?.id;
                    const prevProgressJson = JSON.stringify(prev?.real_photo_progress || {});
                    const nextProgressJson = JSON.stringify(real_photo_progress || {});
                    const shouldUpdate = article
                        && (!prevArticleId || prevArticleId === article.id)
                        && (
                            (Array.isArray(images) && images.length > prevCount)
                            || (Array.isArray(real_images) && real_images.length > prevRealCount)
                            || (prevProgressJson !== nextProgressJson)
                            || (response.data.stage_timings != null && JSON.stringify(prev?.stage_timings) !== JSON.stringify(response.data.stage_timings))
                        );
                    if (shouldUpdate) {
                        return {
                            article,
                            images,
                            real_images: real_images ?? prev?.real_images ?? [],
                            real_photo_progress: real_photo_progress ?? prev?.real_photo_progress ?? null,
                            stage_timings: stage_timings ?? response.data.stage_timings ?? prev?.stage_timings ?? null
                        };
                    }
                    return prev;
                });
                const realCount = Array.isArray(real_images) ? real_images.length : 0;
                const isFailed = article?.status === 'failed';
                const isCompleted = article?.status === 'completed';
                const elapsed = getGenerationElapsedMs();
                const timedOut = elapsed >= GENERATION_FAILSAFE_MS;

                const isDone = isFailed
                    || isCompleted
                    || (targetTotal > 0 && doneCount >= targetTotal && (numRealPhotos <= 0 || realCount >= numRealPhotos));

                if (timedOut && !isDone) {
                    stopImageProgressPolling();
                    try {
                        const snapshotResponse = await generationAPI.getArticle(articleId);
                        const snapshot = snapshotResponse.data;
                        if (snapshot?.article?.status === 'completed') {
                            await handleArticleComplete(snapshot);
                        } else {
                            handleGenerationFailure('Generation timed out. Please retry.', snapshot);
                        }
                    } catch (_) {
                        handleGenerationFailure('Generation timed out. Please retry.');
                    }
                    return;
                }

                if (isDone) {
                    stopImageProgressPolling();
                    if (isFailed) {
                        handleGenerationFailure(article?.status_message || 'Generation failed', {
                            article,
                            images: images || [],
                            real_images: (real_images && real_images.length) ? real_images : [],
                            real_photo_progress: real_photo_progress ?? null,
                            stage_timings: stage_timings ?? null
                        });
                    } else {
                        handleArticleComplete({
                            article,
                            images: images || [],
                            real_images: (real_images && real_images.length) ? real_images : [],
                            real_photo_progress: real_photo_progress ?? null,
                            stage_timings: stage_timings ?? null
                        });
                    }
                }
            } catch (err) {
                console.error('Failed to poll image progress', err);
            }
        };
        poll();
        imageProgressIntervalRef.current = setInterval(poll, 2000);
    };

    const appendStatusUpdate = (statusLabel, message, reset = false) => {
        const defaultMessages = {
            queued: 'Queued for generation',
            searching_context: 'Searching news (Perplexity)...',
            awaiting_news_selection: 'Awaiting news approval...',
            generating_text: 'Drafting article...',
            readability_check: 'Checking readability...',
            rewriting: 'Rewriting to meet readability targets...',
            processing: 'Structuring output...',
            images: `Rendering images (${numImages} requested)...`,
            completed: 'Article and images ready.'
        };
        const effectiveMessage = message || defaultMessages[statusLabel] || '';
        if (!effectiveMessage) return;
        const entry = {
            status: statusLabel,
            message: effectiveMessage,
            timestamp: new Date().toISOString()
        };
        setArticleStatusHistory(prev => {
            const base = reset ? [] : prev;
            if (!reset && base.some(item => item.message === message)) {
                return base;
            }
            return [...base, entry];
        });
        setArticleStatusInfo({ status: statusLabel, message: effectiveMessage });
    };

    const handleGenerationFailure = (message, payload = null) => {
        if (generationFinalizedRef.current) return;
        generationFinalizedRef.current = true;
        stopAllPolling();
        setGenerating(false);
        setArticlePolling(false);
        const errorMessage = message || payload?.article?.status_message || 'Generation failed';
        setArticleError(errorMessage);
        appendStatusUpdate('failed', errorMessage);
        if (payload?.article) {
            setGeneratedContent({
                article: payload.article,
                images: payload.images || [],
                real_images: payload.real_images || payload.realImages || [],
                real_photo_progress: payload.real_photo_progress ?? null,
                stage_timings: payload.stage_timings ?? null
            });
            setCurrentStep(4);
        }
    };

    const handleArticleComplete = async (payload) => {
        if (generationFinalizedRef.current) return;
        generationFinalizedRef.current = true;
        stopAllPolling();
        setArticleError(null);
        setGenerating(false);
        setArticlePolling(false);

        const article = payload?.article || {};
        const articleId = article?.id;
        let images = payload?.images || [];
        let realImages = (payload.real_images && payload.real_images.length)
            ? payload.real_images
            : (payload.realImages && payload.realImages.length)
                ? payload.realImages
                : [];
        let realPhotoProgressState = payload.real_photo_progress || null;
        let stageTimingsState = payload.stage_timings ?? null;

        if (articleId && numRealPhotos > 0 && realImages.length < numRealPhotos) {
            try {
                const r = await generationAPI.getArticle(articleId);
                const fresh = r?.data;
                if (fresh) {
                    images = fresh.images || images;
                    const freshReal = fresh.real_images || fresh.realImages || [];
                    if (freshReal.length > 0) realImages = freshReal;
                    realPhotoProgressState = fresh.real_photo_progress || realPhotoProgressState;
                    if (fresh.stage_timings != null) stageTimingsState = fresh.stage_timings;
                }
            } catch (_) {}
        }
        setImageProgress({
            done: images.length,
            total: numImages,
            status: images.length ? 'Images ready' : 'Images not requested'
        });
        setGeneratedContent({
            ...payload,
            article,
            images,
            real_images: realImages,
            real_photo_progress: realPhotoProgressState,
            stage_timings: stageTimingsState
        });
        appendStatusUpdate('completed', article.status_message || 'Article and images ready.');
        setCurrentStep(4);
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('activity-update'));
        }
        if (articleId) {
            const refetch = () => {
                generationAPI.getArticle(articleId)
                    .then((r) => {
                        const fresh = r.data;
                        if (fresh) {
                            setGeneratedContent((prev) => ({
                                ...fresh,
                                real_images: (fresh.real_images && fresh.real_images.length)
                                    ? fresh.real_images
                                    : (prev?.real_images ?? []),
                                real_photo_progress: fresh.real_photo_progress ?? prev?.real_photo_progress ?? null
                            }));
                            setImageProgress(prev => ({ ...prev, done: (fresh.images || []).length }));
                        }
                    })
                    .catch(() => {});
            };
            const delays = numRealPhotos > 0 ? [500, 1500, 4000, 8000, 12000] : [1500, 4000];
            delays.forEach((ms) => setTimeout(refetch, ms));
        }
    };

    const pollArticleStatus = async (articleId) => {
        if (generationFinalizedRef.current) return;
        try {
            const response = await generationAPI.getArticle(articleId);
            const payload = response.data;
            const { article } = payload;
            setArticleStatusInfo({ status: article.status, message: article.status_message });
            appendStatusUpdate(article.status, article.status_message);
            const isTerminal = article.status === 'completed' || article.status === 'failed';
            const elapsed = getGenerationElapsedMs();

            if (!isTerminal && elapsed >= GENERATION_FAILSAFE_MS) {
                handleGenerationFailure('Generation timed out. Please retry.', payload);
                return;
            }

            if (article.status === 'awaiting_news_selection') {
                setGeneratedContent(payload);
                setGameActive(false);
                setCurrentStep(4);
                setGenerating(false);
                setArticlePolling(false);
                if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                    pollingTimeoutRef.current = null;
                }
                return;
            }
            if (article.status === 'completed') {
                await handleArticleComplete(payload);
            } else if (article.status === 'failed') {
                handleGenerationFailure(article.status_message || 'Generation failed', payload);
            } else {
                if (generationFinalizedRef.current) return;
                if (pollingTimeoutRef.current) {
                    clearTimeout(pollingTimeoutRef.current);
                }
                pollingTimeoutRef.current = setTimeout(() => pollArticleStatus(articleId), 2000);
            }
        } catch (error) {
            console.error('Failed to fetch article status:', error);
            if (generationFinalizedRef.current) return;
            if (getGenerationElapsedMs() >= GENERATION_FAILSAFE_MS) {
                try {
                    const snapshotResponse = await generationAPI.getArticle(articleId);
                    const snapshot = snapshotResponse.data;
                    if (snapshot?.article?.status === 'completed') {
                        await handleArticleComplete(snapshot);
                    } else {
                        handleGenerationFailure('Generation timed out. Please retry.', snapshot);
                    }
                } catch (_) {
                    handleGenerationFailure('Generation timed out. Please retry.');
                }
                return;
            }
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
            pollingTimeoutRef.current = setTimeout(() => pollArticleStatus(articleId), 4000);
        }
    };

    // Template management functions
    const loadTemplates = async (sectionType) => {
        setTemplateLoading(true);
        try {
            const response = await templatesAPI.getAll(sectionType);
            setTemplates(prev => ({ ...prev, [sectionType]: response.data }));
            return response.data;
        } catch (error) {
            console.error('Failed to load templates:', error);
            return [];
        } finally {
            setTemplateLoading(false);
        }
    };

    const openTemplateModal = async (action, section) => {
        setTemplateAction(action);
        setTemplateSection(section);
        setTemplateName('');
        if (action === 'load') {
            await loadTemplates(section);
        }
        setTemplateModalOpen(true);
    };

    const closeTemplateModal = () => {
        setTemplateModalOpen(false);
        setTemplateAction('save');
        setTemplateSection('');
        setTemplateName('');
    };

    const saveTemplate = async () => {
        if (!templateName.trim()) {
            alert('Please enter a template name');
            return;
        }
        if (!templateSection) {
            alert('Select a section to save the template for');
            return;
        }

        try {
            const content = templateSection === 'full'
                ? JSON.stringify(prompts)
                : prompts[templateSection] || '';

            await templatesAPI.create({
                name: templateName,
                template_type: templateSection,
                content: content
            });

            closeTemplateModal();
            alert('Template saved!');
        } catch (error) {
            console.error('Failed to save template:', error);
            alert('Failed to save template');
        }
    };

    const applyTemplate = (template) => {
        if (template.template_type === 'full') {
            try {
                const content = JSON.parse(template.content);
                setPrompts(content);
            } catch (e) {
                console.error('Failed to parse full template');
            }
        } else {
            setPrompts(prev => ({
                ...prev,
                [template.template_type]: template.content
            }));
        }
        closeTemplateModal();
    };

    const deleteTemplate = async (templateId) => {
        if (!confirm('Delete this template?')) return;

        try {
            await templatesAPI.delete(templateId);
            // Reload templates for current section
            await loadTemplates(templateSection);
        } catch (error) {
            console.error('Failed to delete template:', error);
        }
    };

    const savePrompts = async () => {
        if (!selectedBlog) return;

        try {
            await blogsAPI.updatePrompts(selectedBlog.id, prompts);
        } catch (error) {
            console.error('Failed to save prompts:', error);
        }
    };

    const handleContinueToPromo = async () => {
        if (!selectedBlog) return;
        await Promise.all([savePersona(), savePrompts()]);
        setCurrentStep(3);
    };

    const generateArticle = async () => {
        const hasPromoInput = promos.some(p => p.trim());
        const canGenerateWithoutPromo = enableTopicPromoBlocks && articleTopic.trim().length > 0;
        if (!selectedBlog || (!hasPromoInput && !canGenerateWithoutPromo) || articlePolling) return;

        // Close any open preview/chat state before generation
        setPreviewModalOpen(false);
        setPreviewImages([]);
        setPreviewPrompts([]);
        setPreviewId(null);
        setPreviewStyle(null);
        setPreviewLoadingStyleKey(null);
        setStyleChatMessages([]);
        setStyleChatInput('');
        setSuggestedPrompt('');
        setStyleFeedbackLoading(false);
        generationFinalizedRef.current = false;
        generationStartedAtRef.current = Date.now();
        stopAllPolling();

        setGenerating(true);
        setGeneratedContent(null);
        setArticleError(null);
        setArticleStatusHistory([]);
        setArticleStatusInfo(null);
        resetImageProgress();

        try {
            // Save persona + prompts first
            await Promise.all([savePrompts(), savePersona()]);

            const enabledStyleIds = imageStyles
                .filter(s => s.is_enabled !== false && s.id && s.id.length === 36)
                .map(s => s.id);

            const cleanedWhitelist = (researchSettings.perplexity_whitelist || [])
                .map((x) => (x || '').trim())
                .filter(Boolean);
            const cleanedBlacklist = (researchSettings.perplexity_blacklist || [])
                .map((x) => (x || '').trim())
                .filter(Boolean);
            const detailedModeEnabled = enableDetailedPerplexity && !!researchSettings.detailed_mode;
            const effectiveNewsSelectionMode = detailedModeEnabled
                ? 'checkmark'
                : (researchSettings.news_selection_mode || 'auto');

            let rawMode = (researchSettings.perplexity_mode || 'default').toLowerCase();
            if (rawMode === 'unrestricted' || rawMode === '') rawMode = 'default';
            if (rawMode === 'mix') rawMode = 'mixed';
            const perplexityMode = ['strict', 'mixed', 'default'].includes(rawMode) ? rawMode : 'default';
            const researchPayload = {
                perplexity_news_count: researchSettings.perplexity_news_count ?? 12,
                perplexity_whitelist: cleanedWhitelist,
                perplexity_blacklist: cleanedBlacklist,
                perplexity_mode: perplexityMode,
                detailed_mode: detailedModeEnabled,
                time_range_mode: researchSettings.time_range_mode || 'preset',
                recency_preset: researchSettings.recency_preset || '7d',
                search_after_date: researchSettings.search_after_date || '',
                search_before_date: researchSettings.search_before_date || '',
                news_selection_mode: effectiveNewsSelectionMode
            };
            await authAPI.updateResearchSettings(researchPayload).catch(() => {});

            const payloadPromoAttachments = {};
            if (enableTopicPromoBlocks) {
                Object.entries(promoAttachments || {}).forEach(([promoIdx, blockIdx]) => {
                    const p = Number(promoIdx);
                    const b = Number(blockIdx);
                    if (Number.isInteger(p) && Number.isInteger(b) && b > 0) {
                        payloadPromoAttachments[String(p)] = b;
                    }
                });
            }

            const payloadBlockSettings = {};
            Object.entries(blockSettings || {}).forEach(([blockIdx, cfg]) => {
                const b = Number(blockIdx);
                if (!Number.isInteger(b) || b <= 0 || !cfg || typeof cfg !== 'object') return;
                const entry = {};
                const wc = cfg.word_count != null ? Math.round(Number(cfg.word_count)) : null;
                if (wc != null && Number.isFinite(wc) && wc >= 50) entry.word_count = wc;
                const focus = String(cfg.focus || cfg.instructions || '').trim();
                if (focus) entry.instructions = focus.slice(0, 500);
                if (Object.keys(entry).length > 0) payloadBlockSettings[String(b)] = entry;
            });

            const articleResponse = await generationAPI.generateArticle({
                blog_id: selectedBlog.id,
                ai_provider: selectedProvider,
                promos: promos.filter(p => p.trim()),
                topic: (articleTopic && articleTopic.trim()) ? articleTopic.trim() : null,
                structure: articleStructure,
                promo_attachments: enableTopicPromoBlocks ? payloadPromoAttachments : null,
                block_settings: Object.keys(payloadBlockSettings).length > 0 ? payloadBlockSettings : null,
                num_blocks: numBlocks,
                word_count_target: wordCount,
                num_images: numImages,
                num_real_photos: numRealPhotos,
                enabled_style_ids: enabledStyleIds.length > 0 ? enabledStyleIds : null,
                research_settings: researchPayload,
                article_date: articleDate || null,
                aspect_ratio: aspectRatio,
                ...prompts
            });

            const jobArticleId = articleResponse.data.article_id;
            appendStatusUpdate('queued', articleResponse.data.status_message || 'Queued for generation', true);
            setCurrentStep(4);
            setGameActive(true);
            setArticlePolling(true);
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
            }
            pollArticleStatus(jobArticleId);
            if (numImages > 0 || numRealPhotos > 0) {
                startImageProgressPolling(jobArticleId, numImages);
            }
        } catch (error) {
            console.error('Generation failed:', error);
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string'
                ? detail
                : Array.isArray(detail)
                    ? detail.map((d) => d?.msg ?? d?.loc?.join('.') ?? JSON.stringify(d)).join('; ')
                    : detail?.message ?? error.message;
            const fullMsg = msg || 'Generation failed';
            alert('Generation failed: ' + fullMsg);
            setArticleError(fullMsg);
            stopAllPolling();
            setGenerating(false);
            setArticlePolling(false);
        }
    };

    const currentStatusKey = articleStatusInfo?.status || (articleStatusHistory.length > 0 ? articleStatusHistory[articleStatusHistory.length - 1].status : null);
    const statusStepIndex = GENERATION_STEPS.findIndex(step => step.key === currentStatusKey);
    const effectiveStatusIndex = (articlePolling || generating || articleStatusHistory.length > 0)
        ? Math.max(statusStepIndex, 0)
        : statusStepIndex;
    const heroSlots = Math.min(2, Math.max(0, numImages));
    const blockSlots = Math.max(0, numImages - heroSlots);
    const contentStats = generatedContent?.article?.content
        ? computeContentStats(generatedContent.article.content)
        : null;
    const backendStats = {
        word_count: generatedContent?.article?.word_count,
        clean_word_count: generatedContent?.article?.clean_word_count,
        promo_word_count: generatedContent?.article?.promo_word_count,
        promo_percentage: generatedContent?.article?.promo_percentage
    };
    const statsSuspicious =
        contentStats &&
        contentStats.clean_word_count > 10 &&
        ((backendStats.clean_word_count ?? 0) <= 3 ||
            (backendStats.clean_word_count ?? 0) < contentStats.clean_word_count * 0.2);

    const displayWordCount = statsSuspicious
        ? contentStats?.total_words
        : backendStats.word_count ?? contentStats?.total_words ?? null;
    const displayCleanWordCount = statsSuspicious
        ? contentStats?.clean_word_count
        : backendStats.clean_word_count ?? contentStats?.clean_word_count ?? null;
    const displayPromoWordCount = statsSuspicious
        ? contentStats?.promo_word_count
        : backendStats.promo_word_count ?? contentStats?.promo_word_count ?? null;
    const displayPromoPercentage =
        statsSuspicious || backendStats.promo_percentage == null
            ? (displayCleanWordCount > 0 && displayPromoWordCount != null
                ? Math.round((displayPromoWordCount / displayCleanWordCount) * 100)
                : contentStats?.promo_percentage ?? null)
            : backendStats.promo_percentage;
    const realProgressTarget = Number(realPhotoProgress?.target ?? numRealPhotos ?? 0);
    const realProgressSelected = Number(realPhotoProgress?.selected ?? realImagesList.length ?? 0);
    const realProgressPercent = realProgressTarget > 0
        ? Math.min(100, Math.round((realProgressSelected / realProgressTarget) * 100))
        : 0;
    const realProgressStageLabel = realPhotoProgress?.stage
        ? String(realPhotoProgress.stage).replace(/_/g, ' ')
        : null;
    const readabilityMetrics = generatedContent?.article?.readability_metrics || {};
    const readabilityHighlights = readabilityMetrics?.highlights;
    const readabilityGrade = generatedContent?.article?.readability_grade || readabilityMetrics.grade;
    const normalizedReadabilityGrade = typeof readabilityGrade === 'string' ? readabilityGrade.trim().toUpperCase() : '';
    const readabilityTarget = 'A';
    const readabilityPassed = normalizedReadabilityGrade ? normalizedReadabilityGrade.startsWith('A') : false;
    const hasReadabilityData = Boolean(
        readabilityGrade ||
        (readabilityMetrics && Object.keys(readabilityMetrics).length > 0)
    );

    const orderedBlocks = useMemo(() => {
        if (!generatedContent?.article?.content) return [];
        return parseOrderedBlocks(
            generatedContent.article.content,
            generatedContent.article.blocks
        );
    }, [generatedContent?.article?.content, generatedContent?.article?.blocks]);

    const emailPreviewItems = useMemo(() => {
        if (!generatedContent?.article) return [];
        return buildEmailPreviewItems(
            orderedBlocks,
            generatedContent.images || [],
            imageUrls,
            generatedContent.real_images || [],
            realImageUrls
        );
    }, [generatedContent?.article, generatedContent?.images, generatedContent?.real_images, orderedBlocks, imageUrls, realImageUrls]);

    if (loading) {
        return (
            <div className="blog-writer-loading">
                <div className="spinner" />
                <p>Loading your blogs...</p>
            </div>
        );
    }

    return (
        <div className="blog-writer">
            <div className="bw-header">
                <h1>Blog Writer</h1>
                <p>Generate AI-powered articles in your unique style</p>
            </div>

            {/* Progress Steps */}
            <div className="bw-steps">
                {['Select Blog', 'Configure Style', 'Enter Promo', 'Results'].map((step, idx) => (
                    <div
                        key={idx}
                        className={`bw-step ${currentStep > idx ? 'completed' : ''} ${currentStep === idx + 1 ? 'active' : ''}`}
                        onClick={() => currentStep > idx && setCurrentStep(idx + 1)}
                    >
                        <span className="step-number">{idx + 1}</span>
                        <span className="step-label">{step}</span>
                    </div>
                ))}
            </div>

            {/* Step 1: Select Blog */}
            {currentStep === 1 && (
                <div className="bw-section">
                    <h2>Select Your Blog Profile</h2>
                    <div className="blogs-grid">
                        {blogs.map((blog) => (
                            <div
                                key={blog.id}
                                className={`blog-card ${selectedBlog?.id === blog.id ? 'selected' : ''}`}
                            >
                                <button
                                    className="blog-delete"
                                    title="Delete blog"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (deletingBlogId !== blog.id) deleteBlog(blog);
                                    }}
                                    disabled={deletingBlogId === blog.id}
                                >
                                    {deletingBlogId === blog.id ? '…' : '×'}
                                </button>
                                <div
                                    className="blog-clickable"
                                    onClick={() => selectBlog(blog)}
                                >
                                    <div className="blog-avatar">{blog.name.charAt(0)}</div>
                                    <h3>{blog.name}</h3>
                                </div>
                            </div>
                        ))}
                        <div className="blog-card add-new" onClick={() => setShowCreateBlogModal(true)}>
                            <span className="add-icon">+</span>
                            <h3>Add New Blog</h3>
                        </div>
                    </div>

                    {selectedBlog && (
                        <div className="bw-section-footer">
                            <h3>Select AI Model</h3>
                            <div className="providers-grid">
                                {providers.map((provider) => (
                                    <div
                                        key={provider.id}
                                        className={`provider-card ${selectedProvider === provider.id ? 'selected' : ''} ${!provider.available ? 'disabled' : ''}`}
                                        onClick={() => provider.available && setSelectedProvider(provider.id)}
                                    >
                                        <span className="provider-icon">{provider.icon}</span>
                                    <span className="provider-name">{provider.name}</span>
                                    {!provider.available && <span className="coming-soon">Soon</span>}
                                </div>
                                ))}
                            </div>
                            <div className="nav-actions">
                                <button className="btn-primary" onClick={() => setCurrentStep(2)}>
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: Configure Style (5 Prompt Sections) */}
            {currentStep === 2 && (
                <div className="bw-section">
                    <h2>Configure Your Writing Style</h2>
                    <p className="section-desc">Edit each section to fine-tune your unique voice</p>

                    <div className="persona-editor">
                        <div className="persona-header">
                            <div>
                                <h3>Persona & Publication Profile</h3>
                                <p>Everything the model needs to know about the byline and audience.</p>
                            </div>
                            <div className="persona-actions">
                                <button
                                    className="btn-secondary"
                                    onClick={savePersona}
                                    disabled={personaSaving || personaLoading}
                                >
                                    {personaSaving ? 'Saving…' : 'Save Persona'}
                                </button>
                                <button
                                    className="btn-secondary btn-small danger"
                                    onClick={handleDeletePersona}
                                    disabled={personaSaving || personaLoading}
                                >
                                    Delete Persona
                                </button>
                            </div>
                        </div>
                        {personaLoading ? (
                            <div className="panel-loading inline">
                                <span className="spinner" />
                                <p>Loading persona…</p>
                            </div>
                        ) : (
                            <>
                                <div className="persona-grid">
                                    {PERSONA_FIELDS.map((field) => {
                                        const value = personaProfile[field.key] || '';
                                        const onChange = (e) =>
                                            setPersonaProfile((prev) => ({
                                                ...prev,
                                                [field.key]: e.target.value
                                            }));
                                        return (
                                            <div className="persona-field" key={field.key}>
                                                <label>
                                                    <span className="prompt-label">{field.label}</span>
                                                    <span className="prompt-hint">{field.hint}</span>
                                                </label>
                                                {field.type === 'input' ? (
                                                    <input
                                                        type="text"
                                                        value={value}
                                                        onChange={onChange}
                                                        placeholder={`Describe ${field.label.toLowerCase()}`}
                                                    />
                                                ) : (
                                                    <textarea
                                                        value={value}
                                                        onChange={onChange}
                                                        rows={field.key === 'signature_moves' ? 3 : 4}
                                                        placeholder={`Describe ${field.label.toLowerCase()}...`}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="persona-reference">
                                    <div className="persona-reference-header">
                                        <h4>Reference Articles</h4>
                                        <span>Add links or summaries the model should use as inspiration.</span>
                                        <button className="btn-small" onClick={addReferenceArticle}>+ Add</button>
                                    </div>
                                    {referenceArticles.length === 0 ? (
                                        <p className="reference-empty">No reference articles yet. Click + Add to include one.</p>
                                    ) : (
                                        <div className="reference-list">
                                            {referenceArticles.map((article, idx) => (
                                                <div key={idx} className="reference-card editable">
                                                    <div className="form-group">
                                                        <label>Title or Source</label>
                                                        <input
                                                            type="text"
                                                            value={article.title || ''}
                                                            onChange={(e) => updateReferenceArticle(idx, 'title', e.target.value)}
                                                            placeholder="e.g., Bloomberg – AI chips surge"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Summary / URL</label>
                                                        <textarea
                                                            rows={3}
                                                            value={article.summary || article.description || ''}
                                                            onChange={(e) => updateReferenceArticle(idx, 'summary', e.target.value)}
                                                            placeholder="Add a 1-2 sentence summary or a link"
                                                        />
                                                    </div>
                                                    <div className="reference-actions">
                                                        <button className="btn-small danger" onClick={() => removeReferenceArticle(idx)}>Delete</button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="prompts-editor">
                        {[
                            { key: 'tone_of_voice', label: '1. TONE OF VOICE', hint: 'How your writing sounds - warm, analytical, authoritative, etc.' },
                            { key: 'writing_style', label: '2. WRITING STYLE', hint: 'Paragraph structure, sentence length, rhythm' },
                            { key: 'emphasis_formatting', label: '3. EMPHASIS & FORMATTING', hint: 'Bold text rules, italics usage, special markers' },
                            { key: 'language_patterns', label: '4. LANGUAGE PATTERNS', hint: 'Key phrases, transitions, signature expressions' },
                            { key: 'argumentation_style', label: '5. ARGUMENTATION STYLE', hint: 'How you build arguments and present ideas' }
                        ].map(section => (
                            <div className="prompt-section" key={section.key}>
                                <label>
                                    <span className="prompt-label">{section.label}</span>
                                    <span className="prompt-hint">{section.hint}</span>
                                </label>
                                <textarea
                                    value={prompts[section.key]}
                                    onChange={(e) => setPrompts({ ...prompts, [section.key]: e.target.value })}
                                    placeholder={`Describe ${section.label.toLowerCase()}...`}
                                    rows={4}
                                />
                                <div className="template-actions">
                                    <button className="btn-small" onClick={() => openTemplateModal('save', section.key)} title="Save as template">
                                        💾 Save
                                    </button>
                                    <button className="btn-small" onClick={() => openTemplateModal('load', section.key)} title="Load from template">
                                        📂 Load
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bw-actions">
                        <div className="template-full-actions">
                            <button className="btn-small" onClick={() => openTemplateModal('save', 'full')} title="Save all sections as template">
                                💾 Save All Sections
                            </button>
                            <button className="btn-small" onClick={() => openTemplateModal('load', 'full')} title="Load all sections from template">
                                📂 Load Full Template
                            </button>
                        </div>
                        <div className="nav-actions">
                            <button className="btn-secondary" onClick={() => setCurrentStep(1)}>
                                Back
                            </button>
                            <button className="btn-primary" onClick={handleContinueToPromo}>
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Zoom Modal */}
            {zoomImage && (
                <div className="modal-overlay" onClick={() => setZoomImage(null)}>
                    <div className="modal-content image-zoom" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Image details</h3>
                            <button className="modal-close" onClick={() => setZoomImage(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <img src={imageUrls[zoomImage.id] || ''} alt="Zoomed" className="zoomed-img" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                            <div className="zoom-meta">
                                <p><strong>Style:</strong> {zoomImage.style_preset || 'n/a'}</p>
                                <p><strong>Prompt:</strong></p>
                                <textarea
                                    value={zoomImagePrompt}
                                    onChange={(e) => setZoomImagePrompt(e.target.value)}
                                    rows={4}
                                />
                                {zoomImage.id ? (
                                    <button className="btn-primary" onClick={handleRegenerateImage} disabled={regeneratingImage}>
                                        {regeneratingImage ? 'Regenerating...' : 'Regenerate'}
                                    </button>
                                ) : (
                                    <p style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                                        Preview images are view-only; regeneration доступна для картинок статьи.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {realZoomImage && (
                <div className="modal-overlay" onClick={() => setRealZoomImage(null)}>
                    <div className="modal-content image-zoom" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Real photo</h3>
                            <button className="modal-close" onClick={() => setRealZoomImage(null)}>×</button>
                        </div>
                        <div className="modal-body">
                            <img src={realZoomImage.src || ''} alt="Real photo" className="zoomed-img" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                            <div className="zoom-meta">
                                {realZoomImage.placementLabel && (
                                    <p><strong>Placement:</strong> {realZoomImage.placementLabel}</p>
                                )}
                                {realZoomImage.filename && (
                                    <p><strong>Filename:</strong> {realZoomImage.filename}</p>
                                )}
                            </div>
                            <div className="real-edit-panel">
                                <div className="real-edit-header">
                                    <h4>Edit ideas (AI)</h4>
                                    {realEditGenerating && <span className="real-edit-status">Generating...</span>}
                                </div>
                                {realEditLoading ? (
                                    <div className="panel-loading inline">
                                        <span className="spinner" />
                                        <p>Loading suggestions…</p>
                                    </div>
                                ) : (
                                    <>
                                        {realEditError && <p className="readability-error">{realEditError}</p>}
                                        {!realEditLoaded ? (
                                            <div className="real-edit-empty">
                                                <p className="real-edit-hint">Prompts are not generated yet.</p>
                                                <button
                                                    className="btn-secondary"
                                                    onClick={() => realZoomImage?.id && loadRealEditSuggestions(realZoomImage.id, true)}
                                                    disabled={realEditGenerating || !realZoomImage?.id}
                                                >
                                                    Generate prompts
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <p className="real-edit-hint">Pick prompts to generate that many new photos.</p>
                                                <div className="real-edit-list">
                                                    {(realEditPrompts.length ? realEditPrompts : Array(5).fill('')).map((text, idx) => (
                                                        <label key={idx} className="real-edit-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={realEditSelected.has(idx)}
                                                                onChange={(e) => {
                                                                    const next = new Set(realEditSelected);
                                                                    if (e.target.checked) next.add(idx);
                                                                    else next.delete(idx);
                                                                    setRealEditSelected(next);
                                                                    setRealEditLoaded(true);
                                                                    updateRealEditCache(realZoomImage?.id, realEditPrompts, next);
                                                                }}
                                                            />
                                                            <input
                                                                type="text"
                                                                value={text}
                                                                placeholder="Edit prompt..."
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    const nextPrompts = [...(realEditPrompts.length ? realEditPrompts : Array(5).fill(''))];
                                                                    nextPrompts[idx] = value;
                                                                    setRealEditPrompts(nextPrompts);
                                                                    setRealEditLoaded(true);
                                                                    updateRealEditCache(realZoomImage?.id, nextPrompts, realEditSelected);
                                                                }}
                                                            />
                                                        </label>
                                                    ))}
                                                </div>
                                                <div className="real-edit-actions">
                                                    <button
                                                        className="btn-secondary"
                                                        onClick={() => realZoomImage?.id && loadRealEditSuggestions(realZoomImage.id, true)}
                                                        disabled={realEditGenerating || !realZoomImage?.id}
                                                    >
                                                        Regenerate prompts
                                                    </button>
                                                    <button
                                                        className="btn-primary"
                                                        onClick={handleRealEditGenerate}
                                                        disabled={realEditGenerating || !realZoomImage?.id}
                                                    >
                                                        {`Generate${realEditSelected.size ? ` ${realEditSelected.size} photo${realEditSelected.size > 1 ? 's' : ''}` : ''}`}
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3: Enter Promo & Settings */}
            {currentStep === 3 && (
                <div className="bw-section">
                    <h2>Topic, Promo & Generation Settings</h2>

                    <TopicAndDateRow
                        articleTopic={articleTopic}
                        setArticleTopic={setArticleTopic}
                        articleDate={articleDate}
                        setArticleDate={setArticleDate}
                    />

                    <div className="promo-input">
                        <label>
                            <span className="prompt-label">Promotional Materials</span>
                            <span className="prompt-hint">Add 1-6 promotional texts to integrate into the article</span>
                        </label>
                        <div className="promo-fields-container">
                            {promos.map((promo, index) => (
                                <div key={index} className="promo-field-wrapper">
                                    <div className="promo-field-header">
                                        <span className="promo-number">Promo {index + 1}</span>
                                        {promos.length > 1 && (
                                            <button
                                                type="button"
                                                className="btn-ghost danger small"
                                                onClick={() => removePromoField(index)}
                                                title="Remove this promo"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                    <textarea
                                        value={promo}
                                        onChange={(e) => updatePromoField(index, e.target.value)}
                                        placeholder={`Paste promotional material #${index + 1} here...`}
                                        rows={4}
                                    />
                                </div>
                            ))}
                        </div>
                        {promos.length < 6 && (
                            <button
                                type="button"
                                className="btn-secondary small"
                                onClick={addPromoField}
                            >
                                + Add Another Promo
                            </button>
                        )}
                    </div>

                    <div className="settings-dock">
                    <div className="format-settings">
                        <div className="settings-grid">
                            <div className="setting-item">
                                <label>Number of Blocks</label>
                                <select value={numBlocks} onChange={(e) => setNumBlocks(Number(e.target.value))}>
                                    {Array.from({ length: 12 }, (_, idx) => {
                                        const value = idx + 1;
                                        return (
                                            <option key={value} value={value}>
                                                {value} Block{value !== 1 ? 's' : ''}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="setting-item">
                                <label>Target Word Count</label>
                                <select value={wordCount} onChange={(e) => setWordCount(Number(e.target.value))}>
                                    <option value={800}>~800 words</option>
                                    <option value={1000}>~1,000 words</option>
                                    <option value={1200}>~1,200 words</option>
                                    <option value={1500}>~1,500 words</option>
                                    <option value={2000}>~2,000 words</option>
                                    <option value={2500}>~2,500 words</option>
                                    <option value={3000}>~3,000 words</option>
                                    <option value={3500}>~3,500 words</option>
                                    <option value={4000}>~4,000 words</option>
                                    <option value={5000}>~5,000 words</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {settingsDirty && (
                        <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                            <span style={{ color: 'var(--color-warning)', fontSize: '0.8rem', marginRight: '8px' }}>Unsaved changes</span>
                        </div>
                    )}

                    <button
                        type="button"
                        className="btn-secondary"
                        style={{ marginBottom: '12px', width: '100%' }}
                        onClick={doSaveSettings}
                    >
                        Save Settings
                    </button>

                    <ArticleStructureEditor
                        articleStructure={articleStructure}
                        blockSettings={blockSettings}
                        blockSettingsOpen={blockSettingsOpen}
                        promos={promos}
                        promoAttachments={promoAttachments}
                        contentBlockNumbers={contentBlockNumbers}
                        enableTopicPromoBlocks={enableTopicPromoBlocks}
                        insertPromo={insertPromo}
                        resetStructure={resetStructure}
                        updateBlockSetting={updateBlockSetting}
                        toggleBlockSettingsPanel={toggleBlockSettingsPanel}
                        moveBlock={moveBlock}
                        removeIntro={removeIntro}
                        removePromo={removePromo}
                        setPromoAttachment={setPromoAttachment}
                    />

                    <div className="image-settings">
                        <button
                            type="button"
                            className={`settings-dock-toggle ${imageSettingsOpen ? 'open' : ''}`}
                            onClick={() => setImageSettingsOpen((o) => !o)}
                        >
                            Images & Styles
                            <span className="toggle-arrow">{imageSettingsOpen ? '▼' : '▶'}</span>
                        </button>
                        {imageSettingsOpen && (
                        <div className="settings-dock-body">
                        <div className="settings-grid">
                            <div className="setting-item">
                                <label>Number of Images</label>
                                <select value={numImages} onChange={(e) => setNumImages(Number(e.target.value))}>
                                    {Array.from({ length: 21 }, (_, value) => (
                                        <option key={value} value={value}>
                                            {value} {value === 1 ? 'Image' : 'Images'}
                                        </option>
                                    ))}
                                </select>
                                <span className="setting-hint">
                                    = {heroSlots} hero + {blockSlots} block images
                                </span>
                            </div>
                            {enableRealPhotos && (
                                <div className="setting-item">
                                    <label>Number of Real Photos</label>
                                    <select value={numRealPhotos} onChange={(e) => setNumRealPhotos(Number(e.target.value))}>
                                        {Array.from({ length: 11 }, (_, value) => (
                                            <option key={value} value={value}>
                                                {value} {value === 1 ? 'Photo' : 'Photos'}
                                            </option>
                                        ))}
                                    </select>
                                    <span className="setting-hint">
                                        Curated from the web; may take longer
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="ratio-selector">
                            <label>Aspect Ratio</label>
                            <div className="ratio-grid">
                                {ASPECT_RATIOS.map((r) => (
                                    <button
                                        key={r.value}
                                        type="button"
                                        className={`ratio-card${aspectRatio === r.value ? ' selected' : ''}`}
                                        onClick={() => setAspectRatio(r.value)}
                                    >
                                        <div
                                            className="ratio-thumbnail"
                                            style={{ aspectRatio: `${r.w} / ${r.h}` }}
                                        />
                                        <span className="ratio-label">{r.value}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="style-presets">
                            <div className="style-header">
                                <label>Image Styles <span className="style-hint">(click to toggle, pencil to edit)</span></label>
                                <div className="style-header-actions">
                                    <button className="btn-link" onClick={() => openStyleModal(null)}>+ Add Style</button>
                                </div>
                            </div>
                            <div className="styles-grid multi-select">
                                {imageStyles.map((style) => {
                                    const styleKey = style.short_name || style.id;
                                    const isPersistedStyle = Boolean(style.id && style.id.includes('-'));
                                    const isEnabled = style.is_enabled !== false;
                                    return (
                                        <div
                                            key={styleKey}
                                            className={`style-card ${isEnabled ? 'selected' : 'disabled'}`}
                                            onClick={() => toggleStyleActive(style)}
                                            title={style.description || style.prompt?.substring(0, 100)}
                                        >
                                            <div className="style-toggle-row">
                                                <div className="style-card-actions">
                                                    <button
                                                        className="btn-edit-style"
                                                        disabled={!isPersistedStyle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isPersistedStyle) openStyleModal(style);
                                                        }}
                                                        title={isPersistedStyle ? 'Edit style' : 'Save the style first to edit'}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="btn-delete-style"
                                                        disabled={style.is_default || !isPersistedStyle}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isPersistedStyle && !style.is_default) deleteStyle(style);
                                                        }}
                                                        title={style.is_default ? 'Default styles cannot be removed' : 'Delete style'}
                                                    >
                                                        🗑️
                                                    </button>
                                                    <button
                                                        className="btn-secondary btn-small"
                                                        disabled={!isPersistedStyle || previewLoading}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (isPersistedStyle) openStylePreview(style);
                                                        }}
                                                        title="Generate 4 preview images for this style"
                                                    >
                                                        {previewLoading && previewLoadingStyleKey === styleKey ? (
                                                            <>
                                                                <span className="spinner inline" />
                                                                Loading
                                                            </>
                                                        ) : (
                                                            'Try'
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <span className="style-icon">{style.icon || '🖼️'}</span>
                                            <span className="style-name">{style.name}</span>
                                            {isEnabled && (
                                                <span className="style-check">✓</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        </div>
                        )}
                    </div>

                        {typeof researchSettings === 'object' && researchSettings !== null && (
                        <div className={`research-settings-section step3 ${researchSettingsOpen ? 'research-settings-open' : ''}`}>
                            <button
                                type="button"
                                className={`settings-dock-toggle ${researchSettingsOpen ? 'open' : ''}`}
                                onClick={() => setResearchSettingsOpen((o) => !o)}
                            >
                                Research & News
                                <span className="toggle-arrow">{researchSettingsOpen ? '▼' : '▶'}</span>
                            </button>
                            {researchSettingsOpen && (
                                <div className="research-settings-panel">
                                    <div className="setting-item">
                                        <label>Number of news items</label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={30}
                                            className="bw-input"
                                            value={researchSettings.perplexity_news_count ?? 12}
                                            onChange={(e) => setResearchSettings((s) => ({ ...s, perplexity_news_count: Math.min(30, Math.max(1, Number(e.target.value) || 12)) }))}
                                        />
                                    </div>
                                    {enableDetailedPerplexity && (
                                        <div className="setting-item">
                                            <label>
                                                <input
                                                    type="checkbox"
                                                    checked={!!researchSettings.detailed_mode}
                                                    onChange={(e) => setResearchSettings((s) => ({
                                                        ...s,
                                                        detailed_mode: e.target.checked,
                                                        news_selection_mode: e.target.checked ? 'checkmark' : (s.news_selection_mode || 'auto')
                                                    }))}
                                                />
                                                {' '}Detailed Perplexity mode
                                            </label>
                                        </div>
                                    )}
                                    {enableDetailedPerplexity && (
                                        <div className="setting-item">
                                            <label>Source mode</label>
                                            <select
                                                className="bw-input"
                                                value={_normalizePerplexityModeForState(researchSettings.perplexity_mode)}
                                                onChange={(e) => setResearchSettings((s) => ({ ...s, perplexity_mode: e.target.value }))}
                                            >
                                                <option value="strict">Strict (only allowlist)</option>
                                                <option value="mixed">Mixed (prefer allowlist + fallback)</option>
                                                <option value="default">Default (no filter)</option>
                                            </select>
                                        </div>
                                    )}
                                    <div className="setting-item">
                                        <label>Whitelisted sources (one per line)</label>
                                        <textarea
                                            className="bw-input"
                                            rows={3}
                                            value={(researchSettings.perplexity_whitelist || []).join('\n')}
                                            onChange={(e) => setResearchSettings((s) => ({
                                                ...s,
                                                perplexity_whitelist: (e.target.value || '').split(/\n/).map((x) => x.trim())
                                            }))}
                                        />
                                    </div>
                                    {enableDetailedPerplexity && (
                                        <div className="setting-item">
                                            <label>Blacklisted sources (one per line)</label>
                                            <textarea
                                                className="bw-input"
                                                rows={3}
                                                value={(researchSettings.perplexity_blacklist || []).join('\n')}
                                                onChange={(e) => setResearchSettings((s) => ({
                                                    ...s,
                                                    perplexity_blacklist: (e.target.value || '').split(/\n/).map((x) => x.trim())
                                                }))}
                                            />
                                        </div>
                                    )}
                                    {enableDetailedPerplexity && (
                                        <>
                                            <div className="setting-item">
                                                <label>Time range mode</label>
                                                <select
                                                    className="bw-input"
                                                    value={researchSettings.time_range_mode || 'preset'}
                                                    onChange={(e) => setResearchSettings((s) => ({ ...s, time_range_mode: e.target.value }))}
                                                >
                                                    <option value="preset">Preset</option>
                                                    <option value="custom">Custom dates</option>
                                                </select>
                                            </div>
                                            {(researchSettings.time_range_mode || 'preset') === 'preset' ? (
                                                <div className="setting-item">
                                                    <label>Recency preset</label>
                                                    <select
                                                        className="bw-input"
                                                        value={researchSettings.recency_preset || '7d'}
                                                        onChange={(e) => setResearchSettings((s) => ({ ...s, recency_preset: e.target.value }))}
                                                    >
                                                        <option value="24h">last 24 hours</option>
                                                        <option value="7d">last 7 days</option>
                                                        <option value="30d">last 30 days</option>
                                                        <option value="90d">last 90 days</option>
                                                    </select>
                                                </div>
                                            ) : (
                                                <div className="setting-item">
                                                    <label>Custom range</label>
                                                    <div className="date-range-grid">
                                                        <input
                                                            type="date"
                                                            className="bw-input"
                                                            value={researchSettings.search_after_date || ''}
                                                            onChange={(e) => setResearchSettings((s) => ({ ...s, search_after_date: e.target.value }))}
                                                        />
                                                        <input
                                                            type="date"
                                                            className="bw-input"
                                                            value={researchSettings.search_before_date || ''}
                                                            onChange={(e) => setResearchSettings((s) => ({ ...s, search_before_date: e.target.value }))}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className="setting-item">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={enableDetailedPerplexity && researchSettings.detailed_mode
                                                    ? true
                                                    : researchSettings.news_selection_mode === 'checkmark'}
                                                disabled={enableDetailedPerplexity && !!researchSettings.detailed_mode}
                                                onChange={(e) => setResearchSettings((s) => ({
                                                    ...s,
                                                    news_selection_mode: e.target.checked ? 'checkmark' : 'auto'
                                                }))}
                                            />
                                            {' '}Let me choose which news are used (after search)
                                        </label>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-secondary"
                                        disabled={researchSettingsSaving}
                                        onClick={async () => {
                                            setResearchSettingsSaving(true);
                                            try {
                                                await authAPI.updateResearchSettings({
                                                    perplexity_news_count: researchSettings.perplexity_news_count ?? 12,
                                                    perplexity_whitelist: (researchSettings.perplexity_whitelist || []).filter(Boolean),
                                                    perplexity_blacklist: (researchSettings.perplexity_blacklist || []).filter(Boolean),
                                                    perplexity_mode: _normalizePerplexityModeForState(researchSettings.perplexity_mode),
                                                    detailed_mode: !!researchSettings.detailed_mode,
                                                    time_range_mode: (researchSettings.time_range_mode || 'preset') === 'custom' ? 'custom' : 'preset',
                                                    recency_preset: researchSettings.recency_preset || '7d',
                                                    search_after_date: researchSettings.search_after_date || '',
                                                    search_before_date: researchSettings.search_before_date || '',
                                                    news_selection_mode: (enableDetailedPerplexity && researchSettings.detailed_mode)
                                                        ? 'checkmark'
                                                        : (researchSettings.news_selection_mode === 'checkmark' ? 'checkmark' : 'auto')
                                                });
                                            } finally {
                                                setResearchSettingsSaving(false);
                                            }
                                        }}
                                    >
                                        {researchSettingsSaving ? 'Saving...' : 'Save research settings'}
                                    </button>
                                </div>
                            )}
                        </div>
                        )}

                    </div>

                    <div className="bw-actions">
                        <button className="btn-secondary" onClick={() => setCurrentStep(2)}>
                            Back
                        </button>
                        <button
                            className="btn-primary generate-btn"
                            onClick={generateArticle}
                            disabled={(!(promos.some((p) => p.trim()) || (enableTopicPromoBlocks && articleTopic.trim().length > 0)) || generating || articlePolling)}
                        >
                            {articlePolling || generating ? (
                                <>
                                    <span className="spinner" />
                                    Generating…
                                </>
                            ) : (
                                '✨ Generate'
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4: Results */}
            {currentStep === 4 && (generatedContent || gameActive) && (
                <div className="bw-section results-section">
                    <h2>Generated Content</h2>

                    {generatedContent?.article?.status === 'awaiting_news_selection' && (
                        <div className="news-selection-panel">
                            <h3>Select news for this article
                                {generatedContent.news_search_meta && generatedContent.news_search_meta.found < generatedContent.news_search_meta.requested && (
                                    <span className="news-shortfall-badge">
                                        Found {generatedContent.news_search_meta.found} of {generatedContent.news_search_meta.requested} requested
                                    </span>
                                )}
                            </h3>
                            <p className="news-selection-hint news-selection-ttl">Select within 30 minutes or this session will expire.</p>
                            {(generatedContent.article?.news_candidates?.length ?? 0) > 0 ? (
                                <>
                                    <p className="news-selection-hint">Choose which items to include, then click Use selected.</p>
                                    <ul className="news-candidates-list">
                                        {(generatedContent.article.news_candidates || []).map((item, idx) => (
                                            <li key={idx}>
                                                <label>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedNewsIndices.has(idx)}
                                                        onChange={() => {
                                                            setSelectedNewsIndices((prev) => {
                                                                const next = new Set(prev);
                                                                if (next.has(idx)) next.delete(idx);
                                                                else next.add(idx);
                                                                return next;
                                                            });
                                                        }}
                                                    />
                                                    <div className="news-candidate-body">
                                                        <span className="news-candidate-source">{item.source || 'Source'}</span>
                                                        <span className="news-candidate-snippet">{(item.content || '').slice(0, 200)}{(item.content || '').length > 200 ? '…' : ''}</span>
                                                        <div className="news-candidate-meta">
                                                            {item.scope && (
                                                                <span className="news-candidate-scope">{item.scope}</span>
                                                            )}
                                                            {item.published_at && (
                                                                <span className="news-candidate-date">{item.published_at}</span>
                                                            )}
                                                            {item.url && (
                                                                <span className="news-candidate-url" title={item.url}>{item.url}</span>
                                                            )}
                                                            {item.why_matched && (
                                                                <span className="news-candidate-why">{item.why_matched}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="news-selection-actions">
                                        <button
                                            className="btn-primary"
                                            disabled={confirmingNews || (selectedNewsIndices.size === 0)}
                                            onClick={async () => {
                                                if (!generatedContent?.article?.id) return;
                                                setConfirmingNews(true);
                                                try {
                                                    await generationAPI.confirmNews(generatedContent.article.id, {
                                                        selected_indices: Array.from(selectedNewsIndices)
                                                    });
                                                    setGameActive(true);
                                                    setArticlePolling(true);
                                                    pollArticleStatus(generatedContent.article.id);
                                                } finally {
                                                    setConfirmingNews(false);
                                                }
                                            }}
                                        >
                                            {confirmingNews ? 'Resuming...' : `Use selected (${selectedNewsIndices.size})`}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="news-selection-empty">
                                    <p>No news items were found for this search.</p>
                                    <button
                                        className="btn-primary"
                                        disabled={confirmingNews}
                                        onClick={async () => {
                                            if (!generatedContent?.article?.id) return;
                                            if (!confirm('Continue without selected news? The draft may be weaker.')) return;
                                            setConfirmingNews(true);
                                            try {
                                                await generationAPI.confirmNews(generatedContent.article.id, { selected_indices: [] });
                                                setGameActive(true);
                                                setArticlePolling(true);
                                                pollArticleStatus(generatedContent.article.id);
                                            } finally {
                                                setConfirmingNews(false);
                                            }
                                        }}
                                    >
                                        {confirmingNews ? 'Resuming...' : 'Continue without news'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {(gameActive || (generatedContent?.article && generatedContent.article?.status !== 'awaiting_news_selection')) && (
                    <>
                    {(() => {
                        const art = generatedContent?.article;
                        const isTerminal = art?.status === 'completed' || art?.status === 'failed';
                        if (!isTerminal || gameActive) {
                            return (
                                <TetrisGame
                                    isGenerating={!isTerminal}
                                    generationSteps={GENERATION_STEPS}
                                    effectiveStatusIndex={effectiveStatusIndex}
                                    articleReady={art?.status === 'completed'}
                                    articleFailed={art?.status === 'failed'}
                                    statusLog={articleStatusHistory}
                                    onViewResults={() => setGameActive(false)}
                                    onGameOver={(score, meta) => submitGameScore(score, meta)}
                                />
                            );
                        }
                        return null;
                    })()}
                    {(generatedContent?.article?.status === 'completed' || generatedContent?.article?.status === 'failed') && !gameActive && (
                    <div className="results-grid">
                        <div className="result-article">
                            <h3>📝 Article</h3>
                            <div className="article-meta">
                                <span>Title: {generatedContent.article.title || '—'}</span>
                                <span>{displayWordCount ?? '—'} words</span>
                            </div>
                            <div className="article-stats">
                                <span>Clean words: {displayCleanWordCount ?? '—'}</span>
                                <span>Promo words: {displayPromoWordCount ?? '—'}</span>
                                <span>Promo share: {displayPromoPercentage != null ? `${displayPromoPercentage}%` : '—'}</span>
                                {generatedContent.article.rewritten && (
                                    <span>Auto-rewritten</span>
                                )}
                            </div>
                            <div className="readability-panel">
                                {hasReadabilityData ? (
                                    <>
                                        {readabilityMetrics?.error && (
                                            <div className="readability-warning-badge" role="alert" aria-live="polite">
                                                ⚠️ Readable unavailable — article saved without readability grade.
                                            </div>
                                        )}
                                        {readabilityMetrics?.error && (
                                            <p className="readability-error">{readabilityMetrics.error}</p>
                                        )}
                                        <div className="readability-summary-row">
                                            <span className={`readability-pill ${readabilityPassed ? 'pass' : 'fail'}`}>
                                                {readabilityPassed ? '✅ Meets target' : '⚠️ Below target'}
                                            </span>
                                            <span className="readability-grade">
                                                Grade {readabilityGrade || 'N/A'} · Target {readabilityTarget}
                                                {generatedContent.article.rewritten ? ' · Auto-rewritten' : ''}
                                            </span>
                                        </div>
                                        <div className="readability-metrics-grid">
                                            {READABILITY_DISPLAY_METRICS.map((metric) => {
                                                const value = readabilityMetrics?.[metric.key];
                                                if (value === undefined || value === null) return null;
                                                return (
                                                    <div key={metric.key} className="readability-metric">
                                                        <div className="metric-label">{metric.label}</div>
                                                        <div className="metric-value">
                                                            {formatMetricValue(value, metric.decimals ?? 1, metric.suffix || '')}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                ) : (
                                    <p className="readability-fallback">Readability not available (no data from generation)</p>
                                )}
                            </div>
                            <div className="article-content">
                                <pre>{generatedContent.article?.content ?? ''}</pre>
                            </div>
                            <button className="btn-secondary" onClick={() => navigator.clipboard.writeText(generatedContent.article?.content ?? '')}>
                                Copy to Clipboard
                            </button>
                        </div>

                        <div className="result-images">
                            <div className="result-images-header">
                                <h3>🖼️ Generated Images ({(generatedContent.images || []).length})</h3>
                                {(generatedContent.images || []).length > 0 && (
                                    <div className="image-actions">
                                        <button
                                            className="btn-secondary"
                                            onClick={handleUpscaleImages}
                                            disabled={selectedImageIdsForUpscale.size === 0 || upscaling}
                                            title="Re-generate selected images with Gemini 3 Pro Image"
                                        >
                                            {upscaling ? 'Upscaling...' : `⬆️ Upscale (${selectedImageIdsForUpscale.size})`}
                                        </button>
                                        <button
                                            className="btn-secondary"
                                            onClick={handleDownloadAllImages}
                                            disabled={downloadingAll}
                                        >
                                            {downloadingAll ? 'Downloading...' : '⬇️ Download All'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <p className="result-images-hint">Click an image to select for upscale (Gemini 3 Pro); use View to see details.</p>
                            <div className="images-grid">
                                {(generatedContent.images || []).map((img, idx) => {
                                    const key = img.id || `image-${idx}`;
                                    const src = imageUrls[key];
                                    const isSelected = img.id && selectedImageIdsForUpscale.has(img.id);
                                    return (
                                        <div
                                            key={idx}
                                            className={`image-card ${isSelected ? 'selected-for-upscale' : ''}`}
                                            onClick={() => toggleImageForUpscale(img)}
                                        >
                                            {src ? (
                                                <img src={src} alt={`Generated ${idx + 1}`} className="image-preview" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                                            ) : (
                                                <div className="image-placeholder" style={{ aspectRatio: ratioToCss(aspectRatio) }}>Image {idx + 1}</div>
                                            )}
                                            <div className="image-meta">
                                                <span className="image-style">{img.style_preset || 'image'}</span>
                                                <button
                                                    className="btn-small"
                                                    onClick={(e) => { e.stopPropagation(); setZoomImage(img); setZoomImagePrompt(img.prompt || ''); }}
                                                    title="View full size & prompt"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {(enableRealPhotos && numRealPhotos > 0) && (
                            <div className="result-images result-real-photos">
                                <div className="result-images-header">
                                    <h3>📷 Real photos ({realImagesList.length})</h3>
                                </div>
                                <p className="result-images-hint">Click a real photo to view full screen. Placement shown below.</p>
                                {realPhotoProgress && (
                                    <div className="real-photo-progress">
                                        <div className="real-photo-progress-head">
                                            <span>
                                                Stage: {realProgressStageLabel || 'queued'}
                                            </span>
                                            <span>
                                                {realProgressSelected}/{realProgressTarget || numRealPhotos}
                                            </span>
                                        </div>
                                        <div className="real-photo-progress-bar">
                                            <div
                                                className="real-photo-progress-fill"
                                                style={{ width: `${realProgressPercent}%` }}
                                            />
                                        </div>
                                        <div className="real-photo-progress-meta">
                                            <span>
                                                Queries {realPhotoProgress.queries_done ?? 0}/{realPhotoProgress.queries_total ?? 0}
                                            </span>
                                            <span>Downloaded {realPhotoProgress.downloaded ?? 0}</span>
                                            <span>Filtered {realPhotoProgress.filtered ?? 0}</span>
                                            <span>Scored {realPhotoProgress.scored ?? 0}</span>
                                        </div>
                                        {realPhotoProgress.message && (
                                            <div className="real-photo-progress-message">{realPhotoProgress.message}</div>
                                        )}
                                    </div>
                                )}
                                {realImagesList.length === 0 ? (
                                    <div className="real-photos-empty">
                                        <span>Waiting for real photos…</span>
                                        <button
                                            className="btn-secondary btn-small"
                                            onClick={() => generatedContent?.article?.id && generationAPI.getArticle(generatedContent.article.id)
                                                .then((r) => r?.data && setGeneratedContent(r.data))
                                                .catch(() => {})}
                                        >
                                            Refresh
                                        </button>
                                    </div>
                                ) : (
                                    <div className="images-grid">
                                        {realImagesList.map((img, idx) => {
                                            const key = img.id != null ? String(img.id) : `real-${idx}`;
                                            const src = realImageUrls[key];
                                            const placementLabel = img.block_key ? `After ${img.block_key}` : (img.image_number <= 2 ? `Hero ${img.image_number}` : `#${img.image_number}`);
                                            return (
                                                <div
                                                    key={idx}
                                                    className="image-card"
                                                    onClick={() => openRealImageZoom(img, idx)}
                                                >
                                                    {src ? (
                                                        <img src={src} alt={`Real ${idx + 1}`} className="image-preview" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                                                    ) : (
                                                        <div className="image-placeholder" style={{ aspectRatio: ratioToCss(aspectRatio) }}>Real {idx + 1}</div>
                                                    )}
                                                    <div className="image-meta">
                                                        <span className="image-style">{placementLabel}</span>
                                                        <button
                                                            className="btn-small"
                                                            onClick={(e) => { e.stopPropagation(); handleDownloadRealImage(img, idx); }}
                                                            disabled={!img.id}
                                                        >
                                                            Download
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    )}
                    </>
                    )}

                    {generatedContent?.article?.status === 'completed' && emailPreviewItems.length > 0 && (
                        <div className="email-preview-wrap">
                            <h3>Preview as in email</h3>
                            <p className="email-preview-hint">Each image shows where to insert it (Hero 1/2, After BLOCK_N, or Extra). Filename below for download.</p>
                            <div className="email-preview">
                                {emailPreviewItems.map((item, idx) => {
                                    if (item.type === 'hero') {
                                        return (
                                            <div key={`hero-${idx}`} className="email-preview-hero">
                                                {item.images.map((img) => {
                                                    const src = imageUrls[img.id];
                                                    const filename = img.file_path ? img.file_path.split(/[/\\]/).pop() : '';
                                                    const placementLabel = img._placementLabel || `Hero ${img.image_number || ''}`;
                                                    return (
                                                        <div key={img.id || idx} className="email-preview-image-wrap">
                                                            {src ? (
                                                                <img src={src} alt="" className="email-preview-img" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                                                            ) : (
                                                                <div className="email-preview-placeholder" style={{ aspectRatio: ratioToCss(aspectRatio) }}>Image</div>
                                                            )}
                                                            <span className="email-preview-placement">{placementLabel}</span>
                                                            {filename && <span className="email-preview-filename">{filename}</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    }
                                    if (item.type === 'text') {
                                        if (!item.key && !item.text) return null;
                                        return (
                                            <div key={`text-${item.key}-${idx}`} className="email-preview-text">
                                                {item.key && <span className="email-preview-marker">{`===${item.key}===`}</span>}
                                                {item.key && item.text ? <br /> : null}
                                                {item.text}
                                            </div>
                                        );
                                    }
                                    if (item.type === 'image') {
                                        const src = imageUrls[item.image?.id];
                                        return (
                                            <div key={`img-${item.image?.id}-${idx}`} className="email-preview-image-wrap email-preview-single">
                                                {src ? (
                                                    <img src={src} alt="" className="email-preview-img" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                                                ) : (
                                                    <div className="email-preview-placeholder" style={{ aspectRatio: ratioToCss(aspectRatio) }}>Image</div>
                                                )}
                                                {item.placementLabel && <span className="email-preview-placement">{item.placementLabel}</span>}
                                                {item.filename && <span className="email-preview-filename">{item.filename}</span>}
                                            </div>
                                        );
                                    }
                                    if (item.type === 'image_real') {
                                        const src = realImageUrls[item.image?.id];
                                        return (
                                            <div key={`real-${item.image?.id}-${idx}`} className="email-preview-image-wrap email-preview-single email-preview-real">
                                                {src ? (
                                                    <img src={src} alt="" className="email-preview-img" style={{ aspectRatio: ratioToCss(aspectRatio) }} />
                                                ) : (
                                                    <div className="email-preview-placeholder" style={{ aspectRatio: ratioToCss(aspectRatio) }}>Real photo</div>
                                                )}
                                                {item.placementLabel && <span className="email-preview-placement">{item.placementLabel}</span>}
                                                {item.filename && <span className="email-preview-filename">{item.filename}</span>}
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>
                    )}

                    <div className="bw-actions">
                        <button className="btn-secondary" onClick={() => {
                            setGeneratedContent(null);
                            setCurrentStep(3);
                            if (pollingTimeoutRef.current) {
                                clearTimeout(pollingTimeoutRef.current);
                            }
                            setArticleStatusHistory([]);
                            setArticleStatusInfo(null);
                            setArticleError(null);
                            setArticlePolling(false);
                        }}>
                            Generate Another
                        </button>
                        <button className="btn-primary">
                            💾 Save as Template
                        </button>
                    </div>
                </div>
            )}

            {/* Template Modal */}
            {templateModalOpen && (
                <div className="modal-overlay" onClick={closeTemplateModal}>
                    <div className="modal-content template-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>
                                {templateAction === 'save'
                                    ? `Save ${SECTION_LABELS[templateSection] || 'Section'} Template`
                                    : `Load ${SECTION_LABELS[templateSection] || 'Section'} Template`}
                            </h3>
                            <button className="modal-close" onClick={closeTemplateModal}>×</button>
                        </div>
                        <div className="modal-body">
                            {templateAction === 'save' ? (
                                <>
                                    <p className="modal-hint">Give this configuration a friendly name so you can reuse it later.</p>
                                    <div className="form-group">
                                        <label>Template Name</label>
                                        <input
                                            type="text"
                                            value={templateName}
                                            onChange={(e) => setTemplateName(e.target.value)}
                                            placeholder="e.g. Aggressive CTA tone"
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="template-list">
                                    {templateLoading ? (
                                        <div className="template-loading">
                                            <span className="spinner" />
                                            <p>Loading templates...</p>
                                        </div>
                                    ) : (templates[templateSection] || []).length === 0 ? (
                                        <p>No templates saved for this section yet.</p>
                                    ) : (
                                        (templates[templateSection] || []).map((template) => (
                                            <div key={template.id} className="template-card">
                                                <div>
                                                    <h4>{template.name}</h4>
                                                    <p>{new Date(template.created_at).toLocaleString()}</p>
                                                </div>
                                                <div className="template-card-actions">
                                                    <button className="btn-small" onClick={() => applyTemplate(template)}>Apply</button>
                                                    <button className="btn-small danger" onClick={() => deleteTemplate(template.id)}>Delete</button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeTemplateModal}>
                                Close
                            </button>
                            {templateAction === 'save' && (
                                <button className="btn-primary" onClick={saveTemplate}>
                                    Save Template
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Style Edit Modal */}
            {styleModalOpen && (
                <div className="modal-overlay" onClick={closeStyleModal}>
                    <div className="modal-content style-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingStyle ? `Edit Style: ${editingStyle.name}` : 'Create New Style'}</h3>
                            <button className="modal-close" onClick={closeStyleModal}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label>Style Name</label>
                                <input
                                    type="text"
                                    value={styleForm.name}
                                    onChange={(e) => setStyleForm({ ...styleForm, name: e.target.value })}
                                    placeholder="Style name"
                                />
                            </div>
                            <div className="form-group">
                                <label>Short Name <span className="form-hint">(used for quick selection & filenames)</span></label>
                                <input
                                    type="text"
                                    value={styleForm.short_name}
                                    onChange={(e) => setStyleForm({ ...styleForm, short_name: sanitizeShortName(e.target.value) })}
                                    placeholder="e.g. cinematic_hero"
                                    disabled={!!editingStyle}
                                />
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <input
                                    type="text"
                                    value={styleForm.description}
                                    onChange={(e) => setStyleForm({ ...styleForm, description: e.target.value })}
                                    placeholder="Short description"
                                />
                            </div>
                            <div className="form-group">
                                <label>AI Prompt <span className="form-hint">(This text is sent to the AI for image generation)</span></label>
                                <textarea
                                    value={styleForm.prompt}
                                    onChange={(e) => setStyleForm({ ...styleForm, prompt: e.target.value })}
                                    placeholder="Detailed style prompt for AI..."
                                    rows={8}
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={closeStyleModal}>
                                Cancel
                            </button>
                            <button className="btn-primary" onClick={saveStyleChanges}>
                                {editingStyle ? 'Save Changes' : 'Create Style'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Style Preview Panel */}
            {previewModalOpen && (
                <div className="style-preview-panel">
                    <div className="style-preview-left">
                        <h3>{previewStyle ? `Style: ${previewStyle.name}` : 'Style Preview'}</h3>
                        <div className="chat-window tall">
                            {styleChatMessages.map((msg, idx) => (
                                <div key={idx} className={`chat-msg ${msg.role}`}>
                                    <div className="chat-msg-label">{msg.role === 'user' ? 'Your request' : 'AI response'}</div>
                                    <div className="chat-msg-body">{formatStyleMessage(msg.content, msg.role)}</div>
                                </div>
                            ))}
                        </div>
                        <textarea
                            value={styleChatInput}
                            onChange={(e) => setStyleChatInput(e.target.value)}
                            placeholder="Что нравится/не нравится в этом стиле..."
                        />
                        <div className="chat-actions">
                            <button
                                className="btn-secondary"
                                onClick={sendStyleFeedback}
                                disabled={!styleChatInput || previewLoading || styleFeedbackLoading}
                            >
                                {styleFeedbackLoading ? (
                                    <>
                                        <span className="spinner inline" />
                                        Sending...
                                    </>
                                ) : (
                                    'Send feedback'
                                )}
                            </button>
                            <button className="btn-primary" onClick={applySuggestedPrompt} disabled={!suggestedPrompt || applyingPrompt}>
                                {applyingPrompt ? 'Applying...' : '✅ Apply style'}
                            </button>
                            <button className="btn-secondary" onClick={closePreviewModal}>Close</button>
                        </div>
                    </div>
                    <div className="style-preview-right">
                        <div className="preview-status">
                            {previewLoading && (
                                <div className="panel-loading inline">
                                    <span className="spinner" />
                                    Generating previews... ({previewImages.length}/{previewExpectedCount})
                                </div>
                            )}
                            {previewError && <div className="error">{previewError}</div>}
                        </div>
                        <div className="preview-grid tall">
                            {previewImages.map((img, idx) => (
                                <div
                                    key={idx}
                                    className="preview-card"
                                    onClick={() => {
                                        setZoomImage({ id: null, file_path: img.src, prompt: img.prompt, style_preset: previewStyle?.name || 'Preview' });
                                        setZoomImagePrompt(img.prompt || '');
                                    }}
                                >
                                    <img src={img.src} alt={`Preview ${idx + 1}`} />
                                    <pre className="preview-prompt">{img.prompt}</pre>
                                </div>
                            ))}
                            {previewImages.length === 0 && !previewError && (
                                <p className="muted">Waiting for the first preview...</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Create Blog Modal */}
            {showCreateBlogModal && (
                <div className="modal-overlay" onClick={() => setShowCreateBlogModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Create New Blog</h2>
                            <button className="modal-close" onClick={() => setShowCreateBlogModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="blog-name">Blog Name *</label>
                                <input
                                    id="blog-name"
                                    type="text"
                                    value={newBlogName}
                                    onChange={(e) => setNewBlogName(e.target.value)}
                                    placeholder="Enter blog name"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newBlogName.trim()) {
                                            handleCreateBlog();
                                        }
                                    }}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="blog-url">Blog URL (optional)</label>
                                <input
                                    id="blog-url"
                                    type="url"
                                    value={newBlogUrl}
                                    onChange={(e) => setNewBlogUrl(e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setShowCreateBlogModal(false)}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={handleCreateBlog}
                                disabled={creatingBlog || !newBlogName.trim()}
                            >
                                {creatingBlog ? 'Creating...' : 'Create Blog'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BlogWriter;
